/**
 * Client-portal UI strings (en / zh / th).
 *
 * The portal was written with its labels inline in English, several of them hand-built bilingual
 * ("Sign in / เข้าสู่ระบบ"). Those are split here: each locale carries only its own half. A handful
 * keep a Thai gloss in every locale on purpose — where the Thai is the STATUTORY name of a document
 * or form (ใบลดหนี้, 50 ทวิ) rather than a translation, the operator has to see which Thai document
 * they are issuing, because that is the name the server prints on the document face.
 *
 * Nothing here decides tax treatment. Thai wording for the credit/debit-note reasons is copied
 * verbatim from server/src/services/thaiTaxDocs.ts, which is what actually prints on the note.
 */

export const portalEn: Record<string, string> = {
  // admin
  p_admin_title: '{{brand}} Client Portal — Admin',
  // bank
  p_bank_acct_name: 'Account name',
  p_bank_acct_no: 'Account number',
  p_bank_add: '+ Add bank',
  p_bank_address: 'Bank address',
  p_bank_address_hint: '— needed for overseas transfers',
  p_bank_address_ph: '1 Bangkok Blvd, Pathum Wan, Bangkok 10330, Thailand',
  p_bank_confirm_remove: 'Remove this bank account?',
  p_bank_currency: 'Currency',
  p_bank_empty: 'No bank account yet — add one so it prints on your documents.',
  p_bank_name: 'Bank & branch',
  p_bank_name_ph: 'Kasikornbank (KBank), One Bangkok Branch',
  p_bank_no_swift: '⚠ No SWIFT / bank address — overseas payers cannot wire to this account.',
  p_bank_note: 'Note (optional)',
  p_bank_note_ph: 'e.g. for USD invoices only',
  p_bank_qr_alt: 'QR',
  p_bank_qr_hint: 'PromptPay / bank QR — printed in the Payment box on invoices & quotations',
  p_bank_qr_label: 'Payment QR',
  p_bank_qr_title: 'Payment QR printed on documents',
  p_bank_swift: 'SWIFT / BIC',
  p_bank_swift_ph: 'KASITHBK',
  p_bank_title: 'Bank accounts on documents',
  // bills
  p_bills_loading: 'Loading…',
  p_bills_open_pdf: 'Open PDF',
  p_bills_subtitle: 'The quotations, invoices and receipts {{brand}} issued to you.',
  p_bills_tab_invoices: 'Invoices',
  p_bills_tab_quotations: 'Quotations',
  p_bills_tab_receipts: 'Receipts',
  p_bills_th_date: 'Date',
  p_bills_th_due: 'Due',
  p_bills_th_issued_by: 'Issued by',
  p_bills_th_net: 'Net to pay',
  p_bills_th_no: 'No.',
  p_bills_th_paid_on: 'Paid on',
  p_bills_th_status: 'Status',
  p_bills_th_total: 'Total ({{cur}})',
  p_bills_th_valid_until: 'Valid until',
  p_bills_title: 'Bills from {{brand}}',
  p_menu_group_settings: 'Settings',
  p_menu_files: 'Documents', p_menu_files_hint: 'Everything we hold for you, in one place',
  p_files_title: 'Your documents',
  p_files_subtitle: 'Everything {{brand}} holds for you — filed returns, Revenue Department receipts, your company papers and what you have sent in.',
  p_files_loading: 'Loading…', p_files_none: 'Nothing here.', p_files_all: 'All',
  p_files_search: 'Search documents…',
  p_files_name: 'Document', p_files_folder: 'Type', p_files_size: 'Size', p_files_added: 'Added',
  p_files_view: 'View', p_files_download: 'Download',
  p_files_f_tax: 'Tax & filings', p_files_f_company: 'Company papers', p_files_f_uploads: 'You sent us',
  p_files_f_deliverables: 'Delivered to you', p_files_f_workflow: 'Service files', p_files_f_library: 'Other',
  p_files_elsewhere: 'Your invoices and receipts from {{brand}} are under Bills from {{brand}}; the filed returns are also listed under Tax & Filings.',
  p_nav_finance: 'P&L',
  p_fin_title: 'Profit & loss {{year}}',
  p_fin_subtitle: 'From what is actually recorded in your books — no estimates and nothing projected forward.',
  p_fin_loading: 'Loading…',
  p_fin_no_data: 'Nothing recorded for this year yet.',
  p_fin_revenue: 'Revenue',
  p_fin_expenses: 'Expenses',
  p_fin_purchases: 'Purchases',
  p_fin_salaries: 'Wages',
  p_fin_sso: 'Social security (employer share)',
  p_fin_pnd3: 'Fees paid to individuals',
  p_fin_rent53: 'Rent',
  p_fin_expenses_total: 'Total expenses',
  p_fin_net: 'Net',
  p_fin_by_month: 'Month by month',
  p_fin_month: 'Month',
  p_fin_no_payroll: 'payroll not prepared',
  p_fin_incomplete: 'Payroll has not been prepared for month {{months}}, so wages and social security show as zero there. Those months are not final.',
  p_fin_note: 'Revenue and purchases are before VAT. Social security shows the employer half only — the employee half is withheld from wages and is not an extra cost to the company. Fees paid to companies are already inside purchases, so only fees paid to individuals are listed separately.',
  p_nav_calendar: 'Calendar',
  p_cal_title: 'What is coming up',
  p_cal_subtitle: 'Your filing dates and the work on either side. Anything marked "you" is waiting on you.',
  p_cal_loading: 'Loading…', p_cal_nothing: 'Nothing scheduled here.',
  p_cal_all_year: 'Whole year', p_cal_today: 'today', p_cal_you: 'you',
  p_cal_only_mine: 'Only mine ({{count}})', p_cal_yours_count: '{{count}} waiting on you',
  p_cal_is_filing: 'filing deadline', p_cal_done: 'done', p_cal_blocked: 'blocked',
  p_cal_k_pp30: 'VAT (PP.30)', p_cal_k_pnd1: 'PND.1', p_cal_k_pnd3: 'PND.3', p_cal_k_pnd53: 'PND.53',
  p_cal_k_sso: 'Social security', p_cal_k_pnd51: 'Half-year CIT (PND.51)', p_cal_k_pnd50: 'Annual CIT (PND.50)',
  p_cal_k_pnd90: 'Personal income tax', p_cal_k_audit: 'Audit', p_cal_k_annual_fs: 'Annual financial statements',
  p_cal_k_bookkeeping: 'Bookkeeping', p_cal_k_property_tax: 'Property tax',
  p_cal_k_invoice_data_upload: 'Send us your invoices', p_cal_k_tax_payment: 'Pay your tax',
  p_cal_k_service_payment: 'Pay our fee', p_cal_k_service_invoice: 'Our invoice to you',
  p_cal_k_tax_remit: 'We remit your tax', p_cal_k_lead_quotation: 'Quotation',
  p_cal_k_lead_contract: 'Contract', p_cal_k_lead_collect_info: 'Send us your details',
  p_doc_got_bill: 'Received a bill from a supplier?',
  p_doc_got_receipt: 'Got a receipt for something you paid?',
  p_doc_send_to_slf: 'Send it to us to be read and booked →',
  p_nav_hr: 'Staff',
  p_hr_title: 'Your staff',
  p_hr_subtitle: 'Your people and what they cost in social security. To change anything, send us a request — we apply it.',
  p_hr_loading: 'Loading…',
  p_hr_new_request: 'Request a change',
  p_hr_pending_note: '{{count}} request(s) waiting for {{brand}} to review.',
  p_hr_name: 'Name', p_hr_position: 'Position', p_hr_salary: 'Monthly salary',
  p_hr_joined: 'Joined', p_hr_status: 'Status', p_hr_nationality: 'Nationality',
  p_hr_no_staff: 'No staff on record yet.',
  p_hr_active: 'Active', p_hr_left: 'Left', p_hr_not_in_sso: 'Not in social security',
  p_hr_joined_this_month: 'Joined this month',
  p_hr_left_this_month: 'Leaving this month',
  p_hr_change_salary: 'Salary', p_hr_leaving: 'Leaving',
  p_hr_sso_title: 'Social security this month',
  p_hr_sso_how: 'Each side pays {{rate}}% of the monthly wage. Wages below {{floor}} are treated as {{floor}}, and each side is capped at {{cap}} — so above that salary the contribution stops rising. The employee half is withheld from the wage; the employer pays the same again. A month someone was employed in for even one day is a month they are counted for — the month they join and the month they leave both count in full.',
  p_hr_check_these: 'Worth checking this month:',
  p_hr_mover_note: 'Anyone joining or leaving mid-month is where an automatic figure most often needs a person to look. Tell us if it should differ and we will set it by hand.',
  p_hr_employee_side: 'Employee', p_hr_employer_side: 'Employer', p_hr_total: 'Total',
  p_hr_month_total: 'Total · {{count}} people', p_hr_manual: 'set by hand',
  p_hr_no_sso: 'Nobody is in social security for this month.',
  p_hr_snapshot: 'This month is fixed — it is what was filed.',
  p_hr_preview: 'This month is not filed yet, so it is calculated from today\'s staff list and can still change.',
  p_hr_year_title: 'Social security across {{year}}',
  p_hr_month: 'Month', p_hr_headcount: 'People', p_hr_wages: 'Wages',
  p_hr_filed_snap: 'fixed', p_hr_preview_short: 'preview',
  p_hr_requests: 'Your requests',
  p_hr_k_salary_change: 'Salary change', p_hr_k_employee_add: 'New employee', p_hr_k_employee_leave: 'Employee leaving',
  p_hr_s_pending: 'Waiting for {{brand}}', p_hr_s_applied: 'Applied', p_hr_s_rejected: 'Not applied',
  p_hr_request_title: 'Request a change',
  p_hr_request_note: 'This does not change anything by itself. {{brand}} reviews it, because your social security and PND.1 change with it.',
  p_hr_kind: 'What changes', p_hr_employee: 'Employee', p_hr_pick_employee: 'Choose…',
  p_hr_start_date: 'Start date', p_hr_last_day: 'Last working day',
  p_hr_new_salary: 'New monthly salary', p_hr_effective_from: 'Effective from',
  p_hr_note: 'Anything we should know', p_hr_cancel: 'Cancel', p_hr_send: 'Send request', p_hr_sending: 'Sending…',
  p_hr_sso_preview: 'At this salary social security would be {{employee}} from the employee and the same from you — {{total}} in total each month.',
  p_hr_err: 'Could not send that. Please try again.',
  // ── PandaAI Tax (read-only) ──
  p_nav_tax: 'Tax',
  p_tax_title: 'Tax filings',
  p_tax_subtitle: 'Every tax we file for you: how the figure was worked out, what it came to, who has paid what, and the filed paperwork.',
  p_tax_loading: 'Loading…',
  p_tax_pick: 'Choose a filing on the left.',
  p_tax_not_configured: 'Not handled by {{brand}}',
  p_tax_nothing_filed: 'Nothing filed yet',
  p_tax_half_year: 'Filed once per half-year',
  p_tax_annual: 'Filed once per year',
  p_tax_calc: 'How this was worked out',
  p_tax_no_calc: 'No breakdown recorded for this period.',
  p_tax_amount: 'Amount due',
  p_tax_you_paid_slf: 'You paid {{brand}}',
  p_tax_slf_paid_rd: '{{brand}} paid the Revenue Department',
  p_tax_settled: 'Settled in full',
  p_tax_outstanding: 'Still outstanding',
  p_tax_nothing_billed: 'Nothing billed to you for this period',
  p_tax_remitted: 'Remitted',
  p_tax_not_remitted: 'Not yet remitted',
  p_tax_filed: 'Return filed',
  p_tax_method: 'Payment method',
  p_tax_method_na: 'Not recorded',
  p_tax_docs: 'Filed paperwork',
  p_tax_no_docs: 'No documents for this period yet.',
  p_tax_l_out_base: 'Sales (excl. VAT)',
  p_tax_l_out_vat: 'Output VAT',
  p_tax_l_in_base: 'Purchases (excl. VAT)',
  p_tax_l_in_vat: 'Input VAT',
  p_tax_l_net: 'Output − Input',
  p_tax_l_brought: 'Credit brought forward',
  p_tax_l_carry: 'Credit carried to next month',
  p_tax_l_surcharge: 'Surcharge',
  p_tax_l_fines: 'Fines',
  p_tax_l_headcount: 'Employees',
  p_tax_l_gross_wages: 'Gross wages',
  // ── What SLF does (read-only) ──
  p_menu_services: 'Our services',
  p_menu_services_hint: 'What we do, what it costs, and how each one runs',
  p_svc_title: 'What {{brand}} does',
  p_svc_subtitle: 'Our full service list, with your price where one is agreed. Open any service to see how it runs.',
  p_svc_search: 'Search services…',
  p_svc_loading: 'Loading services…',
  p_svc_none: 'No service matches that.',
  p_svc_yours: 'Yours',
  p_svc_your_price: 'Your price',
  p_svc_list_price: 'List price',
  p_svc_how: 'How this service runs:',
  p_svc_steps_loading: 'Loading steps…',
  p_svc_no_steps: 'No steps recorded for this service yet.',
  p_svc_step_you: 'you',
  p_svc_step_slf: '{{brand}}',
  // ── Jobs from SLF (read-only) ──
  p_nav_jobs: 'Jobs',
  p_jobs_title: 'Jobs at {{brand}}',
  p_jobs_subtitle: 'What we are working on for you, and where each one stands. View only.',
  p_jobs_loading: 'Loading jobs…',
  p_jobs_all: 'All',
  p_jobs_no: 'Job',
  p_jobs_what: 'What it is',
  p_jobs_status: 'Status',
  p_jobs_due_date: 'Target / done',
  p_jobs_amount: 'Amount',
  p_jobs_paid: 'Paid',
  p_jobs_outstanding: 'Outstanding',
  p_jobs_date_tbc: 'To be confirmed',
  p_jobs_waiting_on: 'Waiting on',
  p_jobs_opened: 'Opened',
  p_jobs_updated: 'updated',
  p_jobs_st_not_started: 'Not started',
  p_jobs_st_in_progress: 'In progress',
  p_jobs_st_with_you: 'Needs you',
  p_jobs_st_with_authority: 'With a government office',
  p_jobs_st_with_third_party: 'With a third party',
  p_jobs_st_done: 'Completed',
  p_jobs_st_cancelled: 'Cancelled',
  // bs — bank statements the client sends in (owner 2026-10-13)
  p_bs_title: 'Send us your bank statements',
  p_bs_subtitle: 'Statements of your own bank accounts, straight from the bank. {{brand}} matches each line against your invoices and receipts by hand — uploading here changes nothing in your books or your tax return on its own.',
  p_bs_account: 'Bank account',
  p_bs_account_auto: 'Auto-detect from the file',
  p_bs_no_accounts: 'No bank account on your profile yet — add your accounts there first so a statement can be recognised.',
  p_bs_pw_label: 'PDF password',
  p_bs_pw_ph: 'Only if the bank protects the file',
  p_bs_pw_hint: 'Used only to open this file, never stored',
  p_bs_upload: '＋ Upload statements',
  p_bs_uploading: 'Reading…',
  p_bs_file_hint: 'PDF statements from your bank or a CSV export — up to {{max}} files at a time, {{mb}} MB each. Photos and Excel cannot be read.',
  p_bs_too_many: 'Please send at most {{max}} files at a time.',
  p_bs_upload_failed: 'Upload failed',
  p_bs_res_saved: '✓ {{filename}} — account {{account}} · {{rows}} lines read, {{added}} new, {{skipped}} already held',
  p_bs_res_failed: '✗ {{filename}} — {{error}}',
  p_bs_err_account_not_registered: 'the account on this statement is not on your profile yet — add it there first',
  p_bs_err_statement_account_mismatch: 'the statement is for a different account than the one you chose',
  p_bs_list_title: 'Statements received',
  p_bs_th_file: 'File',
  p_bs_th_account: 'Account',
  p_bs_th_period: 'Period',
  p_bs_th_rows: 'Lines',
  p_bs_rows_cell: '{{added}} new of {{rows}}',
  p_bs_closing: 'closing {{amount}}',
  p_bs_th_uploaded: 'Uploaded',
  p_bs_via_portal: 'by you',
  p_bs_via_workspace: 'by {{brand}}',
  p_bs_act_view: 'View',
  p_bs_act_withdraw: 'Withdraw',
  p_bs_confirm_withdraw: 'Withdraw {{filename}}? The lines nobody has matched yet go with it.',
  p_bs_withdraw_locked: 'This statement cannot be withdrawn — {{brand}} has already matched some of its lines.',
  p_bs_empty: 'No statements yet.',
  p_bs_err_open: 'Could not open the file',
  p_bs_file_fallback: 'statement',
  // btn
  p_btn_cancel: 'Cancel',
  p_btn_save: 'Save',
  p_btn_save_bilingual: 'Save',
  // cdoc
  p_cdoc_add_line: '+ line',
  p_cdoc_bank_cash: 'Paid in cash — no bank details',
  p_cdoc_bank_label: 'Bank account printed on this {{docKind}}',
  p_cdoc_bank_none: '— none on file (add one under Profile) —',
  p_cdoc_choose_service: '— choose service —',
  p_cdoc_correct_prompt: 'Why is {{docNo}} being corrected?\n\nThis is recorded against the document together with its previous figures.',
  p_cdoc_currency: 'Currency',
  p_cdoc_remark_ph: 'Notes printed on the document — formatting, line breaks and pasted pictures are kept.',
  p_cdoc_pay_method: 'Payment terms',
  p_cdoc_pay_method_none: '— not stated —',
  p_cdoc_pm_tt: 'T/T — Telegraphic transfer',
  p_cdoc_pm_lc: 'L/C — Letter of credit',
  p_cdoc_pm_sblc: 'SBLC — Standby letter of credit',
  p_cdoc_pm_bg: 'BG — Bank guarantee',
  p_cdoc_pay_method_note: 'Printed in the document header, and written out in full beside the bank details.',
  p_cdoc_pay_method_blank: 'Domestic invoices normally leave this blank. Set it when the deal is settled through a bank instrument.',
  p_cdoc_customer: 'Customer *',
  p_cdoc_doc_type: 'Document type',
  p_cdoc_doc_type_locked: '— fixed once issued',
  p_cdoc_due_date: 'Due date *',
  p_cdoc_due_hint: '— issue + {{days}} days',
  p_cdoc_edit_note: 'The document number stays {{docNo}}. If this document has already been sent to the customer, send them the corrected copy.',
  p_cdoc_entered_incl: 'Entered (VAT incl.) {{amount}} — split below',
  p_cdoc_err_due: 'A due date is required — when is this invoice payable?',
  p_cdoc_err_due_before: 'The due date cannot be before the issue date.',
  p_cdoc_err_line: 'Every line needs a service — choose one, or remove the empty line.',
  p_cdoc_err_reason: 'A reason is required to correct a tax document.',
  p_cdoc_fx_date: 'Rate date',
  p_cdoc_fx_note: 'A tax invoice is normally issued in Thai baht; a foreign currency needs the Revenue Department\'s approval (s.86/4). The THB equivalent is printed on the document.',
  p_cdoc_fx_ph: 'e.g. 33.50',
  p_cdoc_fx_rate: 'Rate to {{cur}} *',
  p_cdoc_issue_btn: 'Issue {{docKind}}',
  p_cdoc_issue_date: 'Issue date',
  p_cdoc_issuing: 'Issuing…',
  p_cdoc_items: 'Items',
  p_cdoc_less_wht: 'Less withholding tax {{amount}}',
  p_cdoc_net: 'Net you receive',
  p_cdoc_qty_title: 'Qty',
  p_cdoc_remark: 'Remark (optional)',
  p_cdoc_save_changes: 'Save changes',
  p_cdoc_saving: 'Saving…',
  p_cdoc_subtotal: 'Subtotal',
  p_cdoc_title_edit: 'Edit {{docKind}} {{docNo}}',
  p_cdoc_title_new: 'New {{docKind}}',
  p_cdoc_total: 'Total',
  p_cdoc_type_invoice: 'Invoice / ใบแจ้งหนี้ — services, tax invoice follows at payment',
  p_cdoc_type_proforma: 'Proforma Invoice — not a tax invoice, no VAT due yet',
  p_cdoc_type_tax_invoice: 'Tax Invoice / Delivery Note · ใบกำกับภาษี/ใบส่งของ — goods, VAT due on delivery',
  p_cdoc_type_tir: 'Tax Invoice / Receipt · ใบกำกับภาษี/ใบเสร็จรับเงิน — cash sale, paid on the spot',
  p_cdoc_unitprice_title: 'Unit price',
  p_cdoc_valid_until: 'Valid until',
  p_cdoc_vat: 'VAT',
  p_cdoc_vat_title: 'VAT treatment',
  // crec
  p_crec_already_paid: 'Already paid',
  p_crec_attach_hint: 'Attach the buyer\'s 50 ทวิ certificate afterwards from the receipt list.',
  p_crec_cash: 'Cash received ({{cur}})',
  p_crec_date_ok: 'Receipt will be dated and numbered in {{yyyymm}}',
  p_crec_date_required: 'Required — check the bank statement, not the day you type this in',
  p_crec_differs: '· differs from the {{amount}} outstanding — this will be recorded as a part payment',
  p_crec_err: 'Failed',
  p_crec_expected_wht: 'Expected WHT',
  p_crec_invoice: 'Invoice *',
  p_crec_invoice_total: 'Invoice total',
  p_crec_issue: 'Issue receipt',
  p_crec_option_outstanding: '{{cur}} {{amount}} outstanding',
  p_crec_outstanding: 'Outstanding',
  p_crec_payment_date: 'Payment date (day the money arrived) *',
  p_crec_saving: 'Saving…',
  p_crec_select_invoice: '— select unpaid invoice —',
  p_crec_settling: 'Settling {{total}} = cash {{cash}} + WHT {{wht}}',
  p_crec_subtitle: 'Choosing an invoice fills in what is still owed and the withholding the buyer is expected to take. Adjust either figure if the money that actually arrived differs.',
  p_crec_title: 'Record a payment',
  p_crec_wht: 'WHT withheld by buyer',
  // cust
  p_cust_add: '+ Add customer',
  p_cust_confirm_remove: 'Remove {{name}}?',
  p_cust_empty: 'No customers yet — add your first buyer.',
  p_cust_f_address: 'Address',
  p_cust_f_branch: 'Branch',
  p_cust_f_email: 'Email',
  p_cust_f_name: 'Company / person name *',
  p_cust_f_name_th: 'Thai name (optional)',
  p_cust_f_notes: 'Notes',
  p_cust_f_notes_hint: '— internal only, not shown on documents',
  p_cust_f_phone: 'Phone',
  p_cust_f_taxid: 'Tax ID',
  p_cust_modal_edit: 'Edit customer',
  p_cust_modal_new: 'New customer',
  p_cust_search_ph: 'Search name, code, tax ID, email…',
  p_cust_th_branch: 'Branch',
  p_cust_th_email: 'Email',
  p_cust_th_name: 'Name',
  p_cust_th_no: 'No.',
  p_cust_th_taxid: 'Tax ID',
  p_cust_title: 'My Customers',
  // disc
  p_disc_accept: 'I have read and accept the above.',
  p_disc_body_bold: 'You are solely responsible for the correctness and continuity of your document numbers',
  p_disc_body_p1: 'All of your sales documents (quotations, invoices, receipts) must be issued through this system so their running numbers stay continuous. Numbers are never reused; cancelling a document keeps its number as VOID.',
  p_disc_body_p2: ', including any documents you issue outside this system.',
  p_disc_continue: 'Continue',
  p_disc_title: 'Document numbering — your responsibility',
  // doc
  p_doc_act_attach_wht: 'Attach 50 ทวิ',
  p_doc_act_attach_wht_title: 'Attach the 50 ทวิ certificate from your buyer',
  p_doc_act_delete: 'Delete & free number',
  p_conv_already: 'Already billed from this quotation: {{list}}. Issuing another is allowed — a quotation can be billed in stages — but your customer will receive a second invoice.',
  p_conv_cancel: 'Cancel',
  p_conv_done: 'Invoice {{docNo}} has been issued.',
  p_conv_go: 'Issue invoice',
  p_conv_loading: 'Checking this quotation…',
  p_conv_receipts: 'Payments already received against this quotation ({{list}}) stay where they are, so the new invoice opens unpaid for its full value. Moving them would rewrite documents your customer already holds.',
  p_conv_subtitle: 'The invoice copies this quotation line for line. It is dated today and starts unpaid.',
  p_conv_title: 'Issue an invoice from {{docNo}}',
  p_conv_type_invoice: 'Invoice',
  p_conv_type_invoice_hint: 'A bill. For services, VAT is due when your customer pays (s.78/1), so the tax invoice is the receipt.',
  p_conv_type_label: 'What kind of document is this?',
  p_conv_type_locked: 'This cannot be changed afterwards \u2014 it sets when the tax is due. To correct it you would have to void the invoice and issue a new one.',
  p_conv_type_proforma: 'Proforma invoice',
  p_conv_type_proforma_hint: 'Not a tax document. Creates no tax point and books nothing.',
  p_conv_type_tax_invoice: 'Tax invoice',
  p_conv_type_tax_invoice_hint: 'For goods, VAT is due on delivery (s.78), so the VAT is booked on this document \u2014 in today\u2019s month.',
  p_conv_working: 'Issuing\u2026',
  p_doc_act_to_invoice: 'Issue invoice',
  p_doc_act_edit: 'Edit',
  p_doc_act_pdf: 'PDF',
  p_doc_act_send: 'Send To…',
  p_doc_act_send_title: 'Email this document to your customer, PDF attached',
  p_doc_act_void: 'Void',
  p_doc_add_new: '+ New {{docKind}}',
  p_doc_add_note: '+ New credit / debit note',
  p_doc_add_receipt: '+ Record payment',
  p_doc_confirm_handed_over: '{{docNo}} has already been downloaded as a PDF, so the buyer may be holding a copy.\n\nEditing it changes what that copy verifies against. If they already have it, issue a credit/debit note instead, or void and reissue.\n\nEdit anyway?',
  p_doc_delete_prompt: 'Delete {{docNo}} and give its number back?\n\nOnly the latest number in a series can be given back. The next document you issue will reuse {{docNo}}.\n\nWhy? (recorded)',
  p_doc_empty: 'Nothing here yet.',
  p_doc_empty_notes: 'No credit or debit notes yet. Issue one from a tax invoice that needs correcting.',
  p_doc_err_open: 'Could not open this document.',
  p_doc_freed: '{{docNo}} is free again — the next document will use it.',
  p_doc_head_invoices: 'invoices',
  p_doc_head_notes: 'notes',
  p_doc_head_quotations: 'quotations',
  p_doc_head_receipts: 'receipts',
  p_doc_kind_invoice: 'invoice',
  p_doc_kind_quotation: 'quotation',
  p_doc_not_editable: 'This document cannot be edited.',
  p_doc_note_credit: 'Credit',
  p_doc_note_debit: 'Debit',
  p_doc_reason_required: 'A reason is required.',
  p_doc_search_ph: 'Search number, customer, address…',
  p_doc_send_tooltip: '{{count}} send(s) · last to {{email}}',
  p_doc_status_open: 'Open',
  p_doc_status_paid: 'Paid',
  p_doc_status_partial: 'Partial',
  p_doc_status_unpaid: 'Unpaid',
  p_doc_status_void: 'VOID',
  p_doc_th_adjusts: 'Adjusts',
  p_doc_th_customer: 'Customer',
  p_doc_th_date: 'Date',
  p_doc_th_difference: 'Difference ({{cur}})',
  p_doc_th_no: 'No.',
  p_doc_th_due: 'Due',
  p_doc_th_owing: 'Outstanding',
  p_sum_n_inv: '{{count}} invoice(s)',
  p_sum_oldest: 'oldest {{days}} days \u2014 {{docNo}}',
  p_sum_outstanding: 'Awaiting payment',
  p_sum_overdue: 'Overdue',
  p_sum_scope: 'All periods, as of today',
  p_doc_th_invoice_no: 'Invoice',
  p_doc_link_self: 'This document',
  p_doc_th_property: 'Property',
  p_doc_th_receipt_no: 'Receipt',
  p_doc_th_received: 'Received ({{cur}})',
  p_doc_th_sent: 'Sent',
  p_doc_th_status: 'Status',
  p_doc_th_total: 'Total',
  p_doc_th_wht: 'WHT',
  p_doc_void_prompt: 'Void {{docNo}}?\n\nThe number stays in the sequence as VOID and is never reused.\n\nWhy is it being voided? (recorded)',
  p_doc_wht_saved: 'WHT certificate saved ✓',
  // empty
  p_empty_no_match: 'No match for “{{query}}”.',
  // erec
  p_erec_bank: 'Bank account shown on the document',
  p_erec_bank_none: '— none —',
  p_erec_received: 'Received {{amount}}',
  p_erec_remark: 'Remark',
  p_erec_saving: 'Saving…',
  p_erec_title: 'Edit receipt {{docNo}}',
  p_erec_warning: 'This receipt is a tax invoice. The customer, the amount, the VAT and the date are what the buyer claims input tax on, so they cannot be changed here — to correct any of those, void this receipt and record the payment again.',
  p_erec_wht: '· WHT {{amount}}',
  // err
  p_err_create_failed: 'Create failed',
  p_err_save_failed: 'Save failed',
  // exp
  p_exp_act_remove: 'Remove',
  p_exp_act_rescan: 'Re-read figures',
  p_exp_act_rescan_title: 'Read the amounts off this scan again',
  p_exp_act_view: 'View attachment',
  p_exp_confirm_remove: 'Remove {{filename}}?',
  p_exp_empty: 'Nothing uploaded for {{month}} {{year}} yet.',
  p_exp_err_open: 'Could not open the file',
  p_exp_file_fallback: 'file',
  p_exp_file_hint: 'Photos and PDF only — Word and Excel cannot be read; please export to PDF. A multi-page invoice should be sent as one PDF, not as separate photos.',
  p_exp_filed_warn: 'The VAT return for {{month}} {{year}} has already been filed. Anything you send for this month will be entered as an additional submission, not into the return that was already sent.',
  p_exp_filed_warn_bold: 'additional submission',
  p_exp_filter_all: 'All statuses',
  p_exp_filter_approved: 'Entered in your books',
  p_exp_filter_pending: 'Waiting for {{brand}}',
  p_exp_filter_rejected: 'Returned to you',
  p_exp_list_title: 'Uploaded — {{month}} {{year}}',
  p_exp_month: 'Month',
  p_exp_msg_appended: '· {{filename}} — added as page {{page}} of invoice {{docNo}}',
  p_exp_msg_ocr_error: 'Some files could not be read automatically — {{brand}} will type the figures in by hand.',
  p_exp_msg_saved: '{{n}} invoice(s) received and waiting for {{brand}}.',
  p_exp_msg_skipped: '{{n}} skipped as duplicates:',
  p_exp_msg_skipped_line: '· {{filename}}{{docNo}} — {{why}}',
  p_exp_no_vat: 'No VAT',
  p_exp_ocr_error_row: 'Could not be read automatically',
  p_exp_page_chip: 'p{{n}}',
  p_exp_page_title: 'Page {{n}}',
  p_exp_quota_unlimited: '{{used}} uploaded for {{month}} {{year}} · no monthly limit',
  p_exp_quota_full: 'Monthly limit of {{cap}} reached for {{month}} {{year}} — for a larger batch please email {{contact}}',
  p_exp_quota_left: '{{used}} of {{cap}} used for {{month}} {{year}} · {{left}} left',
  p_exp_reading: 'Reading…',
  p_exp_search_ph: 'Search supplier, invoice no, file…',
  p_exp_status_approved: 'Entered in your books',
  p_exp_status_pending: 'Waiting for {{brand}}',
  p_exp_status_rejected: 'Returned to you',
  p_exp_subtitle: 'Photos or PDF of the bills you paid. {{brand}} checks each one against the scan before it goes into your books — nothing here changes your tax return on its own.',
  p_exp_th_amount: 'Amount',
  p_exp_th_attachment: 'Attachment',
  p_exp_th_bucket: 'Bucket',
  p_exp_th_date: 'Date',
  p_exp_th_docno: 'Invoice no.',
  p_exp_th_status: 'Status',
  p_exp_th_supplier: 'Supplier',
  p_exp_th_vat: 'VAT',
  p_exp_title: 'Send us your purchase invoices',
  p_exp_upload: '＋ Upload invoices',
  p_exp_upload_failed: 'Upload failed',
  p_exp_with_vat: 'With VAT',
  p_exp_year: 'Year',
  // fil
  p_fil_chip_additional: 'A{{n}}',
  p_fil_chip_attachment: 'Attachment',
  p_fil_chip_form: 'Form',
  p_fil_chip_receipt: 'Receipt',
  p_fil_legend: 'Green = filed, documents available below · Form = the filed return · Receipt = the Revenue Department payment receipt · ✓ = filed, document not uploaded yet. Missing something? Contact {{brand}} and we will add it.',
  p_fil_loading: 'Loading…',
  p_fil_subtitle: 'The returns {{brand}} filed for you, month by month. Click a month to open the filed form or the Revenue Department receipt.',
  p_fil_th_filing: 'Filing',
  p_fil_title: 'Tax & Filings',
  // filing
  p_filing_doc_gone: 'This document is no longer available. Please contact {{brand}}.',
  // img
  p_img_err_generic: 'Could not read that image',
  p_img_err_not_image: 'That file is not an image',
  p_img_err_read: 'Could not read the file',
  p_img_none: 'None',
  p_img_remove: 'Remove',
  p_img_replace: 'Replace',
  p_img_upload: 'Upload',
  // imp
  p_imp_already_bold: '{{n}} document number(s) already exist',
  p_imp_already_rest: 'and will stop the import: {{list}}',
  p_imp_btn: 'Import',
  p_imp_btn_n: 'Import {{n}}',
  p_imp_confirm: 'Create {{n}} {{docTypeWord}} using the document numbers in the sheet?\n\n{{m}} new customer record(s) will be created.\n\nThis cannot be undone in bulk — each document would have to be voided one by one.',
  p_imp_create_as: 'Create as',
  p_imp_done: '✓ {{created}} invoice(s) created · {{customers}} new customer(s)',
  p_imp_done_btn: 'Done',
  p_imp_err_failed: 'Import failed',
  p_imp_err_read: 'Could not read that file.',
  p_imp_open: 'Import from spreadsheet',
  p_imp_map_title: 'Match the spreadsheet columns to our fields',
  p_imp_map_saved: '· using the mapping you saved for this sheet',
  p_imp_map_edit: 'Edit mapping',
  p_imp_map_hide: 'Hide mapping',
  p_imp_map_th_column: 'Spreadsheet column',
  p_imp_map_th_sample: 'Example values',
  p_imp_map_th_field: 'Becomes',
  p_imp_map_th_vat: 'VAT treatment',
  p_imp_map_th_label: 'Invoice line name',
  p_imp_map_no_header: 'Column {{n}} (no header)',
  p_imp_map_required: 'Nothing is mapped to "{{field}}" — every document needs it.',
  p_imp_map_need_amount: 'Map at least one column of amounts — that is what becomes the invoice lines.',
  p_imp_field_ignore: 'Do not import',
  p_imp_field_customer: 'Customer name',
  p_imp_field_docNo: 'Invoice number',
  p_imp_field_issueDate: 'Issue date',
  p_imp_field_dueDate: 'Due date',
  p_imp_field_address: 'Address on the document',
  p_imp_field_description: 'Description',
  p_imp_field_remark: 'Remark',
  p_imp_field_fee: 'Amount (an invoice line)',
  p_imp_field_total: "Sheet's own total (cross-check only)",
  p_imp_fee_q: 'How is each fee column taxed?',
  p_imp_fee_warn: '— getting this wrong changes what every buyer pays by 7%',
  p_imp_file_label: 'Spreadsheet (.xlsx)',
  p_imp_header_incl: '· header says “include VAT”',
  p_imp_header_unknown: '· header does not say — please confirm',
  p_imp_mismatch_body: 'The invoice will be issued for the fee columns above; the sheet’s total may be measuring something different. Check a few before importing:',
  p_imp_mismatch_bold: '{{n}} row(s) do not reconcile to the sheet’s own total column.',
  p_imp_mismatch_line: 'row {{n}} · {{docNo}} · invoice {{a}} vs sheet {{b}}',
  p_imp_more: '… and {{n}} more',
  p_imp_prev_th_customer: 'Customer',
  p_imp_prev_th_date: 'Date',
  p_imp_prev_th_lines: 'Lines',
  p_imp_prev_th_total: 'Total',
  p_imp_sheet: 'Sheet',
  p_imp_skipped_head: 'Skipped — these will NOT be imported',
  p_imp_skipped_row: 'row {{n}}',
  p_imp_skipped_title: 'Skipped rows — nothing was created for these:',
  p_imp_subtitle: 'One row per invoice. The document numbers, dates and amounts come from your file exactly as they are — nothing is generated or adjusted.',
  p_imp_summary: '{{rows}} invoice(s) will be created · {{customers}} new customer(s) · total {{amount}} {{cur}}',
  p_imp_summary_skipped: '{{skipped}} row(s) skipped',
  p_imp_th_docno: 'Invoice no.',
  p_imp_th_owner: 'Owner',
  p_imp_th_property: 'Property',
  p_imp_th_row: 'Row',
  p_imp_th_why: 'Why',
  p_imp_title: 'Import invoices from a spreadsheet',
  p_imp_type_inv: 'Invoice — billing only, tax invoice follows at payment',
  p_imp_type_ti: 'Tax Invoice / Delivery Note — VAT due on delivery',
  p_imp_type_tir: 'Tax Invoice / Receipt — cash sale, already settled',
  p_imp_word_invoices: 'invoices',
  p_imp_word_tax_invoices: 'tax invoices',
  p_imp_working: 'Working…',
  // lang
  p_lang_en: 'English',
  p_lang_en_short: 'EN',
  p_lang_th_short: 'ไทย',
  p_lang_zh_short: '中',
  // loading
  p_loading: 'Loading…',
  // login
  p_login_email: 'Email',
  p_login_err_failed: 'Login failed',
  p_login_err_staff: 'This is the client portal — staff should use the internal system.',
  // Cross-installation sign-in (owner 2026-10-15): the hop is shown and confirmed before the
  // password goes anywhere but this build's own host.
  p_login_err_blank: 'Enter your email and password.',
  p_login_hop_text: 'This sign-in belongs to another office of the firm ({{host}}). Continue there?',
  p_login_hop_go: 'Yes, sign in there',
  p_login_hop_cancel: 'Cancel',
  p_login_password: 'Password',
  p_login_signin: 'Sign in',
  p_login_signing_in: 'Signing in…',
  p_login_subtitle: 'Invoicing & billing for your business',
  p_login_title: '{{brand}} Client Portal',
  // logo
  p_logo_alt: '{{brand}}',
  // menu
  p_menu_actions_tooltip: 'Actions',
  p_menu_bills: 'Bills from {{brand}}',
  p_menu_bills_hint: 'What {{brand}} has charged you',
  p_menu_customers: 'Customers',
  p_menu_customers_hint: 'The buyers you invoice',
  p_menu_filings: 'Tax & Filings',
  p_menu_filings_hint: 'The returns {{brand}} filed for you',
  p_menu_group_fromslf: 'From {{brand}}',
  p_menu_group_mydata: 'My data',
  p_menu_group_company: 'Company',
  p_menu_products: 'Products & Services',
  p_menu_products_hint: 'What you sell',
  p_menu_profile: 'Company Profile',
  p_menu_profile_hint: 'Address, logo, document numbering',
  p_menu_tooltip: 'Menu',
  p_menu_working: 'Working…',
  // month
  p_month_apr: 'Apr',
  p_month_aug: 'Aug',
  p_month_dec: 'Dec',
  p_month_feb: 'Feb',
  p_month_jan: 'Jan',
  p_month_jul: 'Jul',
  p_month_jun: 'Jun',
  p_month_mar: 'Mar',
  p_month_may: 'May',
  p_month_nov: 'Nov',
  p_month_oct: 'Oct',
  p_month_sep: 'Sep',
  // nav
  p_nav_bankstmt: 'Bank Statements',
  p_nav_expenses: 'Purchase Invoices',
  p_office_label: 'Your {{brand}} office',
  p_office_hint: 'The {{brand}} office that keeps your books and issues your documents',
  p_nav_owner: 'Owner console',
  p_own_title: 'Owner console',
  p_own_lede: 'Your business at a glance — what you are owed, what you owe, and what falls due next.',
  p_own_empty: 'Nothing to show here yet. This page fills in as you issue invoices and as we file on your behalf.',
  p_own_owed_you: 'OWED TO YOU',
  p_own_overdue_suffix: 'overdue',
  p_own_chase: 'OLDEST UNPAID',
  p_own_days_late: '{{days}} days past due',
  p_own_owed_slf: 'YOU OWE {{brand}}',
  p_own_next_due: 'Next due {{date}}',
  p_own_waiting: 'WAITING ON YOU',
  p_own_and_more: '+{{n}} more',
  p_own_task_upload: 'Send us this month\u2019s paperwork',
  p_own_task_tax_pay: 'Pay tax',
  p_own_task_svc_pay: 'Pay our fee',
  p_own_task_info: 'Send us the information we asked for',
  p_own_upcoming: 'DUE NEXT',
  p_own_today: 'today',
  p_own_in_days: 'in {{days}}d',
  p_own_amount_tbc: 'amount to come',
  p_own_amount_credit: '{{amount}} credit c/f',
  p_own_efiling_note: 'These are the Revenue Department e-filing deadlines, which fall later than the over-the-counter dates.',
  p_own_booked: 'BOOKED IN {{year}}',
  p_own_revenue: 'Sales',
  p_own_purchases: 'Purchases',
  p_own_booked_note: 'From the {{months}} month(s) recorded so far. This is not a profit figure — payroll only counts once a month has been closed.',
  p_own_filed: 'FILED FOR YOU IN {{year}}',
  p_own_filed_note: 'Returns we have submitted',
  p_nav_books: 'My books',
  p_menu_company_find: 'Find a company…',
  p_menu_books_locked: 'Appears once your books are opened',
  p_books_period: 'Accounting period',
  p_menu_company_none: 'No company matches that.',
  p_menu_company_n_of: '{{n}} of {{total}}',
  p_menu_policy: 'How we keep your books',
  p_menu_policy_hint: 'The treatment we actually apply to your records',
  p_menu_chart: 'Chart of accounts',
  p_menu_chart_hint: 'The accounts your books are kept in',
  p_menu_ledgers: 'Ledger & trial balance',
  p_menu_ledgers_hint: 'The five journals, the ledgers and the trial balance',
  p_books_no_entries: 'No entries this period',
  p_menu_statements: 'Financial statements',
  p_menu_statements_hint: 'Statement of financial position, profit or loss, changes in equity',
  p_pol_title: 'How we keep your books',
  p_pol_lede: 'What our system actually does with your records. This describes the rules the software applies — it is not a statement of Thai law. Where law is involved, your accountant\u2019s written advice governs.',
  p_pol_readonly: 'This page is read-only.',
  p_pol_s1_t: 'When your sale enters your books',
  p_pol_s1_b: 'A TAX INVOICE (including a tax invoice / receipt) is recorded as output in the month the document is issued. A PLAIN INVOICE is recorded only once payment is received and a receipt has been entered. So a plain invoice left unpaid this month does not appear in this month\u2019s VAT basis — and if it is never paid, it never appears.',
  p_pol_s2_t: 'When withholding tax is recorded',
  p_pol_s2_b: 'Tax withheld by your customer is recorded when the money arrives, not in the month of the invoice. Your receivable therefore shows the gross amount, not the sum you will actually bank.',
  p_pol_s3_t: 'Credit and debit notes',
  p_pol_s3_b: 'To change the value of an invoice already issued we raise a separate credit or debit note, recorded in its own month. The original invoice\u2019s record is never altered — that trail is what an auditor looks for.',
  p_pol_s4_t: 'Foreign currency',
  p_pol_s4_b: 'We hold no exchange rates of our own. On a foreign-currency document the rate is entered per document by whoever raises it, and any rate needed at year end must be supplied to us. Without a rate we decline to convert — we will not estimate one. Foreign-currency receivables are therefore shown per currency and never combined into a $t(cur_adj) total.',
  p_pol_s5_t: 'Input VAT you cannot reclaim',
  p_pol_s5_b: 'Input VAT that cannot be reclaimed is charged to expense account 5710, rather than left among recoverable input tax.',
  p_pol_s6_t: 'Valuing your stock',
  p_pol_s6_b: 'Two cost formulas are supported: FIFO and weighted average. Once a period has been valued on one, we cannot switch you to the other. Where the stock records do not reconcile — an issue with no matching receipt, or more going out than came in — we decline to produce a valuation rather than give you a figure with nothing behind it.',
  p_pol_s7_t: 'Depreciation',
  p_pol_s7_b: 'Depreciation is calculated straight line and prorated by the days you actually owned the asset within the period, over a 365-day year. It never takes an asset below its residual value, and it never posts itself: every charge is prepared as a draft and reviewed by a person before it enters your books. The ceilings we work to are below.',
  p_pol_s7_note: 'Rates are maximums. Where an asset is given no rate of its own, the maximum for its class is applied.',
  p_pol_s8_t: 'Tax losses carried forward',
  p_pol_s8_b: 'We keep your carried-forward losses year by year. The expiry column reads \u201cnot established\u201d, because we hold no source we can cite for it. Recording a limit requires recording its legal basis at the same time.',
  p_pol_s9_t: 'What happens to a document you send us',
  p_pol_s9_b: 'Every document is filed as one of three kinds. It first becomes a draft entry, reviewed by a person before it is posted. A posted entry is never deleted — an error is corrected by a visible reversing entry. Every entry lands in one of five named journals, and bank entries name the account they belong to.',
  p_pol_th_class: 'Asset class',
  p_pol_th_rate: 'Maximum rate',
  p_pol_not_dep: 'not depreciable',
  p_books_none_t: 'Your books have not been written up yet',
  p_books_none_b: 'Once we have opened an accounting period for you and posted the first entries, your chart of accounts, ledgers and financial statements appear here.',
  p_nav_invoices: 'Invoices',
  p_nav_notes: 'Credit / Debit Notes',
  p_nav_quotations: 'Quotations',
  p_nav_receipts: 'Receipts',
  p_nav_sign_out: 'Sign out',
  // note
  p_note_amount_ph: 'Amount',
  p_note_difference_label: 'Difference in value',
  p_note_err: 'Could not issue the note',
  p_note_info: 'Enter only the difference this note adjusts — not the whole original amount. The VAT on that difference moves your output tax in the month this note is issued.',
  p_note_issue_btn: 'Issue note',
  p_note_issue_date: 'Issue date',
  p_note_issuing: 'Issuing…',
  p_note_item_ph: 'What is being adjusted',
  p_note_kind_credit: 'credit note (ใบลดหนี้)',
  p_note_kind_debit: 'debit note (ใบเพิ่มหนี้)',
  p_note_no_sources: 'No tax invoice on file yet — a note can only adjust a document that carried VAT.',
  p_note_opt_credit: 'Credit note — reduce the value (ใบลดหนี้)',
  p_note_opt_debit: 'Debit note — increase the value (ใบเพิ่มหนี้)',
  p_note_reason_discount: 'Discount granted after the tax invoice (ลดราคาภายหลัง)',
  p_note_reason_extra_goods: 'Additional goods or services delivered (ส่งมอบเพิ่มเติม)',
  p_note_reason_goods_defective: 'Goods defective / not as ordered (สินค้าชำรุดบกพร่อง)',
  p_note_reason_goods_returned: 'Goods returned (สินค้าถูกส่งคืน)',
  p_note_reason_label: 'Reason * (printed on the note)',
  p_note_reason_other: 'Other (อื่น ๆ)',
  p_note_reason_other_ph: 'Describe the reason',
  p_note_reason_overcharge: 'Value overstated on the original (คำนวณราคาสูงกว่าที่เป็นจริง)',
  p_note_reason_service_not_provided: 'Service partly or wholly not provided (บอกเลิกการให้บริการ)',
  p_note_reason_undercharge: 'Value understated on the original (คำนวณราคาต่ำกว่าที่เป็นจริง)',
  p_note_ref: 'Tax invoice being adjusted *',
  p_note_title_new: 'New {{noteKind}}',
  p_note_total_difference: 'Difference',
  p_note_type: 'Type *',
  // pager
  p_pager_first: 'First page',
  p_pager_last: 'Last page',
  p_pager_next: 'Next',
  p_pager_no_records: 'No records',
  p_pager_page_of: 'Page {{page}} of {{pages}}',
  p_pager_per_page: 'Per page',
  p_pager_prev: 'Previous',
  p_pager_showing: 'Showing {{from}}–{{to}} of {{total}}',
  // pdf
  p_pdf_failed: 'PDF failed',
  // prod
  p_prod_add: '+ Add item',
  p_prod_confirm_remove: 'Remove {{name}}?',
  p_prod_empty: 'No items yet — add what you sell.',
  p_prod_f_name: 'Name *',
  p_prod_f_price: 'Unit price ({{cur}})',
  p_prod_f_unit: 'Unit (e.g. pcs, hr)',
  p_prod_f_vat_checkbox: 'Subject to 7% VAT',
  p_prod_f_wht: 'Default withholding tax (WHT)',
  p_prod_f_wht_hint: 'Goods normally 0%; services 3%; rent 5%. The buyer deducts it and gives you a 50 ทวิ certificate.',
  p_prod_modal_edit: 'Edit item',
  p_prod_modal_new: 'New item',
  p_prod_photo_hint: 'shown in your catalog list',
  p_prod_photo_label: 'Photo (optional)',
  p_prod_search_ph: 'Search name or unit…',
  p_prod_th_name: 'Name',
  p_prod_th_price: 'Price ({{cur}})',
  p_prod_th_unit: 'Unit',
  p_prod_th_vat: 'VAT',
  p_prod_title: 'My Products & Services',
  p_prod_vat_yes: '{{rate}}%',
  // prof
  p_prof_address: 'Address on documents',
  p_prof_biz_goods: 'Goods — VAT is due on delivery, so the tax invoice goes out with the goods (s.78)',
  p_prof_biz_hint: 'This only sets the default document type when you create one — you can change it on each document.',
  p_prof_biz_mixed: 'Both goods and services',
  p_prof_biz_service: 'Services — VAT is due when the customer pays (s.78/1)',
  p_prof_biz_type: 'What you sell',
  p_prof_company_name: 'Company name (locked)',
  p_prof_default_wht: 'Default WHT on new lines',
  p_prof_default_wht_hint: 'Used when a line has no catalog item of its own.',
  p_prof_doc_cn: 'Credit note',
  p_prof_doc_dn: 'Debit note',
  p_prof_doc_inv: 'Invoice',
  p_prof_doc_pro: 'Proforma',
  p_prof_doc_quo: 'Quotation',
  p_prof_doc_rec: 'Receipt',
  p_prof_doc_tax: 'Tax invoice',
  p_prof_email: 'Email',
  p_prof_failed: 'Failed',
  p_prof_identity_note: 'Identity fields are verified from your company documents and can only be changed by our team.',
  p_prof_logo_hint: 'printed top-left on invoices, quotations and receipts',
  p_prof_logo_label: 'Company logo',
  p_prof_month_seg: 'Include the year-month in document numbers',
  p_prof_month_seg_hint: '— on: INV-{{yyyymm}}-001 · off: INV-001. Turn this off if your own numbers already contain the period.',
  p_prof_numbering: 'Document numbering',
  p_prof_numbering_note: '— continuity is your responsibility (accepted {{date}})',
  p_prof_phone: 'Phone',
  p_prof_prefix_next: '{{docType}} prefix / next number',
  p_prof_pw_changed: 'Password changed ✓',
  p_prof_saved: 'Saved ✓',
  p_prof_branch: 'Branch (locked)',
  p_prof_branch_head: 'Head Office',
  p_prof_branch_hint: 'Printed next to your Tax ID on tax invoices and credit / debit notes.',
  p_prof_branch_n: 'Branch',
  p_prof_tax_id: 'Tax ID (locked)',
  p_prof_title: 'Company Profile',
  p_prof_vat_hint: 'Registered ⇒ your invoices add 7% VAT and your receipt is a tax invoice.',
  p_prof_vat_not_registered: 'Not registered',
  p_prof_vat_reg: 'VAT registration',
  p_prof_vat_registered: 'Registered (7% VAT)',
  p_prof_website: 'Website',
  // pw
  p_pw_confirm: 'Confirm new password',
  p_pw_current: 'Current password',
  p_pw_err_current_required: 'Enter your current password.',
  p_pw_err_failed: 'Failed',
  p_pw_err_min_length: 'At least 8 characters.',
  p_pw_err_mismatch: 'Passwords do not match.',
  p_pw_hint_current: 'Your current password is required.',
  p_pw_hint_first: 'For security, please replace the temporary password.',
  p_pw_new: 'New password',
  p_pw_title_change: 'Change password',
  p_pw_title_first: 'Set your own password',
  // row
  p_row_delete: 'Delete',
  p_row_edit: 'Edit',
  // search
  p_search_clear: 'Clear',
  // select
  p_select_placeholder: '— select —',
  p_pick_search: 'Type to search…',
  p_pick_none: 'No match — try fewer words.',
  p_pick_more: 'Showing {{shown}} of {{total}} — type to search the rest.',
  p_cust_add_title: 'Add customer',
  p_cust_add_save: 'Add & select',
  p_cust_name_req: 'Customer name is required.',
  p_cust_add_failed: 'Could not add the customer.',
  p_prof_f_prefix: 'Prefix',
  p_prof_f_next: 'Next number',
  p_prof_f_date: 'Date in number',
  p_prof_f_reset: 'Restart',
  p_prof_f_pad: 'Digits',
  p_prof_date_none: 'No date',
  p_prof_reset_none: 'Never',
  p_prof_reset_year: 'Every year',
  p_prof_reset_month: 'Every month',
  p_prof_reset_day: 'Every day',
  p_prof_scheme_bad: 'Add {{need}} to the number, or the same number comes round again next period.',
  p_prof_numbering_hint: 'The number is prefix · date · running number. Restarting the run needs a date fine enough to tell one period from the next — yearly needs YYYY, monthly YYYYMM, daily YYYYMMDD. Changing any of this affects only documents issued from now on; everything already issued keeps its number.',
  p_flt_all: 'All',
  p_flt_custom: 'Custom',
  p_flt_clear: 'Clear the date filter',
  p_flt_period: 'Period',
  p_flt_show_voided: 'Show voided',
  p_imp_open_short: 'Import',
  p_menu_notes_hint: 'Correct a document already issued',
  p_flt_this_month: 'This month',
  p_flt_last_month: 'Last month',
  p_flt_from: 'From date',
  p_flt_to: 'To date',
  // send
  p_send_attachment: 'Attachment',
  p_send_bcc: 'Copy to you',
  p_send_btn: 'Send email',
  p_send_done: 'Done',
  p_send_err_prepare: 'Could not prepare the email.',
  p_send_err_send: 'The email could not be sent.',
  p_send_from: 'From',
  p_send_history: 'Previously sent',
  p_send_message: 'Message',
  p_send_no_replyto: '— add your company email under Profile first —',
  p_send_preparing: 'Preparing…',
  p_send_preview: 'Preview',
  p_send_preview_hint: 'Opens the exact PDF your customer will receive — both sheets, Original and Copy.',
  p_send_replyto: 'Reply to',
  p_send_save_addr: 'Save this address to {{customerName}} so it fills in next time',
  p_send_sending: 'Sending…',
  p_send_sent_ok: '✓ Sent to {{email}}. A copy is in your own inbox.',
  p_send_subject: 'Subject',
  p_send_subtitle: 'The PDF is attached automatically. Your customer’s reply comes straight back to you.',
  p_send_title: 'Send {{docNo}}',
  p_send_to_label: 'Customer email *',
  p_send_to_ph: 'name@example.com',
  // sort
  p_sort_th_tooltip: 'Sort by {{label}}',
  // vat
  p_vat_hint_exempt: 'Exempt supply (s.81).',
  p_vat_hint_none: 'Out of scope / not a VAT registrant.',
  p_vat_hint_standard: 'VAT is added on top of the price you enter.',
  p_vat_hint_standard_incl: 'The price you enter already includes VAT.',
  p_vat_hint_zero: 'Zero-rated (s.80/1) — taxable at 0%, not the same as exempt.',
  p_vat_opt_exempt: 'VAT Exempt',
  p_vat_opt_none: 'No VAT',
  p_vat_opt_standard: 'VAT {{rate}}% — Exclusive',
  p_vat_opt_standard_incl: 'VAT {{rate}}% — Inclusive',
  p_vat_opt_zero: 'VAT 0%',
  // wht
  p_wht_none: 'No WHT',
  p_wht_rate: 'WHT {{rate}}%',
  p_wht_select_title: 'Withholding tax the buyer deducts',
};

export const portalZh: Record<string, string> = {
  // admin
  p_admin_title: '{{brand}} 客户门户 — 管理端',
  // bank
  p_bank_acct_name: '账户名称',
  p_bank_acct_no: '账号',
  p_bank_add: '+ 添加银行账户',
  p_bank_address: '银行地址',
  p_bank_address_hint: '— 境外汇款需要',
  p_bank_address_ph: '1 Bangkok Blvd, Pathum Wan, Bangkok 10330, Thailand',
  p_bank_confirm_remove: '确定删除该银行账户?',
  p_bank_currency: '币种',
  p_bank_empty: '尚未添加银行账户 — 添加后会打印在单据上。',
  p_bank_name: '银行及分行',
  p_bank_name_ph: '开泰银行(KBank)One Bangkok 支行',
  p_bank_no_swift: '⚠ 未填 SWIFT / 银行地址 — 境外付款方无法电汇到此账户。',
  p_bank_note: '备注(选填)',
  p_bank_note_ph: '例如:仅用于 USD 发票',
  p_bank_qr_alt: 'QR',
  p_bank_qr_hint: 'PromptPay / 银行二维码 — 打印在发票和报价单的付款栏内',
  p_bank_qr_label: '收款二维码',
  p_bank_qr_title: '打印在单据上的收款二维码',
  p_bank_swift: 'SWIFT / BIC',
  p_bank_swift_ph: 'KASITHBK',
  p_bank_title: '单据上的银行账户',
  // bills
  p_bills_loading: '加载中…',
  p_bills_open_pdf: '打开 PDF',
  p_bills_subtitle: '{{brand}} 向您开具的报价单、发票和收据。',
  p_bills_tab_invoices: '发票',
  p_bills_tab_quotations: '报价单',
  p_bills_tab_receipts: '收据',
  p_bills_th_date: '日期',
  p_bills_th_due: '到期',
  p_bills_th_issued_by: '开具方',
  p_bills_th_net: '应付净额',
  p_bills_th_no: '编号',
  p_bills_th_paid_on: '付款日期',
  p_bills_th_status: '状态',
  p_bills_th_total: '合计({{cur}})',
  p_bills_th_valid_until: '有效期至',
  p_bills_title: '{{brand}} 账单',
  p_menu_group_settings: '设置',
  p_menu_files: '文件', p_menu_files_hint: '我们为您保管的全部文件,集中在一处',
  p_files_title: '您的文件',
  p_files_subtitle: '{{brand}} 为您保管的全部文件 —— 申报表、税务局回执、公司证照,以及您发给我们的单据。',
  p_files_loading: '正在加载…', p_files_none: '这里暂无文件。', p_files_all: '全部',
  p_files_search: '搜索文件…',
  p_files_name: '文件', p_files_folder: '类别', p_files_size: '大小', p_files_added: '加入时间',
  p_files_view: '查看', p_files_download: '下载',
  p_files_f_tax: '税务与申报', p_files_f_company: '公司证照', p_files_f_uploads: '您发给我们的',
  p_files_f_deliverables: '交付给您的', p_files_f_workflow: '服务过程文件', p_files_f_library: '其他',
  p_files_elsewhere: '{{brand}} 开给您的发票和收据在「{{brand}} 账单」里;申报表在「税务申报」里也能找到。',
  p_nav_finance: '损益',
  p_fin_title: '{{year}} 年损益表',
  p_fin_subtitle: '取自账上真实记录的数据 —— 不含预估,也不向后推算。',
  p_fin_loading: '正在加载…',
  p_fin_no_data: '本年度暂无记录。',
  p_fin_revenue: '收入',
  p_fin_expenses: '支出',
  p_fin_purchases: '采购',
  p_fin_salaries: '工资',
  p_fin_sso: '社保(公司部分)',
  p_fin_pnd3: '支付给个人的费用',
  p_fin_rent53: '租金',
  p_fin_expenses_total: '支出合计',
  p_fin_net: '净额',
  p_fin_by_month: '按月明细',
  p_fin_month: '月份',
  p_fin_no_payroll: '工资未生成',
  p_fin_incomplete: '{{months}} 月的工资尚未生成,因此这些月份的工资和社保显示为 0。这几个月的数字不是最终数。',
  p_fin_note: '收入和采购均为不含税金额。社保只显示公司承担的部分 —— 个人部分从工资中代扣,不是公司的额外成本。支付给公司的费用已包含在采购里,所以只单列支付给个人的费用。',
  p_nav_calendar: '日程',
  p_cal_title: '接下来要办的事',
  p_cal_subtitle: '您的申报期限,以及双方各自要做的事。标着「您」的都在等您。',
  p_cal_loading: '正在加载…', p_cal_nothing: '这里暂无安排。',
  p_cal_all_year: '全年', p_cal_today: '今天', p_cal_you: '您',
  p_cal_only_mine: '只看我的({{count}})', p_cal_yours_count: '{{count}} 件在等您',
  p_cal_is_filing: '申报期限', p_cal_done: '已完成', p_cal_blocked: '受阻',
  p_cal_k_pp30: '增值税 (PP.30)', p_cal_k_pnd1: 'PND.1', p_cal_k_pnd3: 'PND.3', p_cal_k_pnd53: 'PND.53',
  p_cal_k_sso: '社会保险', p_cal_k_pnd51: '半年度企业所得税 (PND.51)', p_cal_k_pnd50: '年度企业所得税 (PND.50)',
  p_cal_k_pnd90: '个人所得税', p_cal_k_audit: '审计', p_cal_k_annual_fs: '年度财务报表',
  p_cal_k_bookkeeping: '记账', p_cal_k_property_tax: '房产税',
  p_cal_k_invoice_data_upload: '把发票发给我们', p_cal_k_tax_payment: '缴纳税款',
  p_cal_k_service_payment: '支付我们的服务费', p_cal_k_service_invoice: '我们开给您的账单',
  p_cal_k_tax_remit: '我们代您缴税', p_cal_k_lead_quotation: '报价',
  p_cal_k_lead_contract: '合同', p_cal_k_lead_collect_info: '把资料发给我们',
  p_doc_got_bill: '收到供应商开来的发票?',
  p_doc_got_receipt: '有您付款后取得的收据?',
  p_doc_send_to_slf: '发给我们识别并入账 →',
  p_nav_hr: '员工',
  p_hr_title: '您的员工',
  p_hr_subtitle: '您的员工名册,以及各自的社保金额。需要变更请提交申请,由我们代为执行。',
  p_hr_loading: '正在加载…',
  p_hr_new_request: '提交变更申请',
  p_hr_pending_note: '有 {{count}} 条申请正在等待 {{brand}} 审核。',
  p_hr_name: '姓名', p_hr_position: '职位', p_hr_salary: '月薪',
  p_hr_joined: '入职', p_hr_status: '状态', p_hr_nationality: '国籍',
  p_hr_no_staff: '暂无员工记录。',
  p_hr_active: '在职', p_hr_left: '已离职', p_hr_not_in_sso: '不在社保',
  p_hr_joined_this_month: '本月入职',
  p_hr_left_this_month: '本月离职',
  p_hr_change_salary: '调薪', p_hr_leaving: '离职',
  p_hr_sso_title: '本月社保',
  p_hr_sso_how: '劳资双方各按月薪的 {{rate}}% 缴纳。月薪低于 {{floor}} 按 {{floor}} 计,每方上限 {{cap}} —— 超过该薪资后缴费不再增加。个人部分从工资中代扣,公司再缴同等金额。只要当月在职过哪怕一天,就按整月计算 —— 入职当月和离职当月都算。',
  p_hr_check_these: '本月需要留意:',
  p_hr_mover_note: '当月入职或离职的人,最容易需要人工确认。如果金额应当不同,告诉我们,我们手工设定。',
  p_hr_employee_side: '个人', p_hr_employer_side: '公司', p_hr_total: '合计',
  p_hr_month_total: '合计 · {{count}} 人', p_hr_manual: '手工设定',
  p_hr_no_sso: '本月没有需要缴纳社保的员工。',
  p_hr_snapshot: '本月已固定 —— 这是当时申报的数据。',
  p_hr_preview: '本月尚未申报,按今天的员工名册试算,仍可能变化。',
  p_hr_year_title: '{{year}} 年全年社保',
  p_hr_month: '月份', p_hr_headcount: '人数', p_hr_wages: '工资总额',
  p_hr_filed_snap: '已固定', p_hr_preview_short: '试算',
  p_hr_requests: '您的申请',
  p_hr_k_salary_change: '调整工资', p_hr_k_employee_add: '新员工入职', p_hr_k_employee_leave: '员工离职',
  p_hr_s_pending: '等待 {{brand}} 处理', p_hr_s_applied: '已执行', p_hr_s_rejected: '未执行',
  p_hr_request_title: '提交变更申请',
  p_hr_request_note: '提交本身不会改动任何数据。{{brand}} 会先审核,因为您的社保和 PND.1 会随之变化。',
  p_hr_kind: '变更类型', p_hr_employee: '员工', p_hr_pick_employee: '请选择…',
  p_hr_start_date: '入职日期', p_hr_last_day: '最后工作日',
  p_hr_new_salary: '新月薪', p_hr_effective_from: '生效日期',
  p_hr_note: '需要我们知道的情况', p_hr_cancel: '取消', p_hr_send: '提交申请', p_hr_sending: '正在提交…',
  p_hr_sso_preview: '按此薪资,社保为个人 {{employee}}、公司同额,每月合计 {{total}}。',
  p_hr_err: '提交失败,请重试。',
  // ── PandaAI Tax(只读)──
  p_nav_tax: '税务',
  p_tax_title: '税务申报',
  p_tax_subtitle: '我们为您申报的每一项税:数字怎么算出来的、金额多少、谁付了什么、以及最终的申报文件。',
  p_tax_loading: '正在加载…',
  p_tax_pick: '请在左侧选择一个税种。',
  p_tax_not_configured: '{{brand}} 不办理此项',
  p_tax_nothing_filed: '尚无申报记录',
  p_tax_half_year: '半年申报一次',
  p_tax_annual: '一年申报一次',
  p_tax_calc: '计算过程',
  p_tax_no_calc: '本期没有计算明细。',
  p_tax_amount: '应缴金额',
  p_tax_you_paid_slf: '您付给 {{brand}}',
  p_tax_slf_paid_rd: '{{brand}} 付给税务局',
  p_tax_settled: '已全额结清',
  p_tax_outstanding: '尚欠',
  p_tax_nothing_billed: '本期未向您计费',
  p_tax_remitted: '已缴纳',
  p_tax_not_remitted: '尚未缴纳',
  p_tax_filed: '申报表已提交',
  p_tax_method: '支付方式',
  p_tax_method_na: '系统未记录',
  p_tax_docs: '申报文件',
  p_tax_no_docs: '本期暂无文件。',
  p_tax_l_out_base: '销售额(不含税)',
  p_tax_l_out_vat: '销项税',
  p_tax_l_in_base: '采购额(不含税)',
  p_tax_l_in_vat: '进项税',
  p_tax_l_net: '销项 − 进项',
  p_tax_l_brought: '上期结转留抵',
  p_tax_l_carry: '结转下期留抵',
  p_tax_l_surcharge: '滞纳金',
  p_tax_l_fines: '罚款',
  p_tax_l_headcount: '员工人数',
  p_tax_l_gross_wages: '工资总额',
  // ── SLF 服务介绍(只读)──
  p_menu_services: '服务介绍',
  p_menu_services_hint: '我们做什么、多少钱、流程怎么走',
  p_svc_title: '{{brand}} 提供的服务',
  p_svc_subtitle: '完整服务清单。已与您约定价格的显示您的价格。点开任一服务可看到办理流程。',
  p_svc_search: '搜索服务…',
  p_svc_loading: '正在加载…',
  p_svc_none: '没有匹配的服务。',
  p_svc_yours: '您已订',
  p_svc_your_price: '您的价格',
  p_svc_list_price: '目录价',
  p_svc_how: '这项服务怎么走:',
  p_svc_steps_loading: '正在加载流程…',
  p_svc_no_steps: '这项服务暂未录入流程。',
  p_svc_step_you: '您',
  p_svc_step_slf: '{{brand}}',
  // ── SLF 业务进度(只读)──
  p_nav_jobs: '业务进度',
  p_jobs_title: '{{brand}} 业务进度',
  p_jobs_subtitle: '我们正在为您办理的事项,以及各自进行到哪一步。仅供查看。',
  p_jobs_loading: '正在加载…',
  p_jobs_all: '全部',
  p_jobs_no: '编号',
  p_jobs_what: '事项',
  p_jobs_status: '状态',
  p_jobs_due_date: '预计 / 完成',
  p_jobs_amount: '金额',
  p_jobs_paid: '已付',
  p_jobs_outstanding: '未付',
  p_jobs_date_tbc: '待定',
  p_jobs_waiting_on: '等待',
  p_jobs_opened: '受理于',
  p_jobs_updated: '更新于',
  p_jobs_st_not_started: '尚未开始',
  p_jobs_st_in_progress: '进行中',
  p_jobs_st_with_you: '等您提供',
  p_jobs_st_with_authority: '等政府部门',
  p_jobs_st_with_third_party: '等第三方',
  p_jobs_st_done: '已完成',
  p_jobs_st_cancelled: '已取消',
  // bs — bank statements the client sends in (owner 2026-10-13)
  p_bs_title: '请上传您的银行流水',
  p_bs_subtitle: '您自己银行账户的对账单,直接来自银行。{{brand}} 会逐行与您的发票、收据人工核对 — 这里上传的内容本身不会改变您的账簿或报税。',
  p_bs_account: '银行账户',
  p_bs_account_auto: '从文件自动识别',
  p_bs_no_accounts: '您的资料中尚无银行账户 — 请先在公司资料中添加账户,流水才能被识别。',
  p_bs_pw_label: 'PDF 密码',
  p_bs_pw_ph: '仅当银行给文件加了密码时填写',
  p_bs_pw_hint: '仅用于打开此文件,不会保存',
  p_bs_upload: '＋ 上传流水',
  p_bs_uploading: '读取中…',
  p_bs_file_hint: '银行出具的 PDF 对账单或 CSV 导出文件 — 每次最多 {{max}} 个文件,每个不超过 {{mb}} MB。照片和 Excel 无法读取。',
  p_bs_too_many: '每次最多上传 {{max}} 个文件。',
  p_bs_upload_failed: '上传失败',
  p_bs_res_saved: '✓ {{filename}} — 账户 {{account}} · 读取 {{rows}} 行,新增 {{added}} 行,{{skipped}} 行已存在',
  p_bs_res_failed: '✗ {{filename}} — {{error}}',
  p_bs_err_account_not_registered: '该流水所属账户尚未登记在您的资料中 — 请先添加该账户',
  p_bs_err_statement_account_mismatch: '该流水所属账户与您选择的账户不一致',
  p_bs_list_title: '已收到的流水',
  p_bs_th_file: '文件',
  p_bs_th_account: '账户',
  p_bs_th_period: '期间',
  p_bs_th_rows: '行数',
  p_bs_rows_cell: '{{rows}} 行,新增 {{added}}',
  p_bs_closing: '期末余额 {{amount}}',
  p_bs_th_uploaded: '上传时间',
  p_bs_via_portal: '您上传',
  p_bs_via_workspace: '{{brand}} 上传',
  p_bs_act_view: '查看',
  p_bs_act_withdraw: '撤回',
  p_bs_confirm_withdraw: '撤回 {{filename}}?其中尚未核对的行将一并移除。',
  p_bs_withdraw_locked: '无法撤回该流水 — {{brand}} 已核对了其中部分行。',
  p_bs_empty: '尚无流水。',
  p_bs_err_open: '无法打开该文件',
  p_bs_file_fallback: '流水',
  // btn
  p_btn_cancel: '取消',
  p_btn_save: '保存',
  p_btn_save_bilingual: '保存',
  // cdoc
  p_cdoc_add_line: '+ 添加行',
  p_cdoc_bank_cash: '现金支付 —— 不显示银行信息',
  p_cdoc_bank_label: '打印在此 {{docKind}} 上的银行账户',
  p_cdoc_bank_none: '— 尚未添加(请在公司资料中添加)—',
  p_cdoc_choose_service: '— 选择服务 —',
  p_cdoc_correct_prompt: '为什么要更正 {{docNo}}?\n\n此说明会连同修改前的数据一并记录在该单据上。',
  p_cdoc_currency: '币种',
  p_cdoc_remark_ph: '打印在单据上的备注 — 格式、换行和粘贴的图片都会保留。',
  p_cdoc_pay_method: '付款方式',
  p_cdoc_pay_method_none: '— 未注明 —',
  p_cdoc_pm_tt: 'T/T — 电汇',
  p_cdoc_pm_lc: 'L/C — 信用证',
  p_cdoc_pm_sblc: 'SBLC — 备用信用证',
  p_cdoc_pm_bg: 'BG — 银行保函',
  p_cdoc_pay_method_note: '会打印在单据抬头，并在银行信息旁写出全称。',
  p_cdoc_pay_method_blank: '境内发票通常留空；以银行工具结算时才需要选择。',
  p_cdoc_customer: '客户 *',
  p_cdoc_doc_type: '单据类型',
  p_cdoc_doc_type_locked: '— 开具后不可更改',
  p_cdoc_due_date: '到期日 *',
  p_cdoc_due_hint: '— 开具日 + {{days}} 天',
  p_cdoc_edit_note: '单据编号仍为 {{docNo}}。若本单据已发给客户,请将更正后的单据再发一次。',
  p_cdoc_entered_incl: '录入金额(含 VAT){{amount}} — 拆分见下',
  p_cdoc_err_due: '必须填写到期日 — 此发票何时应付?',
  p_cdoc_err_due_before: '到期日不能早于开具日期。',
  p_cdoc_err_line: '每一行都必须选择服务项目 —— 请选择,或删除空行。',
  p_cdoc_err_reason: '更正税务单据必须填写原因。',
  p_cdoc_fx_date: '汇率日期',
  p_cdoc_fx_note: '税务发票通常应以泰铢开具;使用外币需经税务局批准 (s.86/4)。单据上会打印折合泰铢的金额。',
  p_cdoc_fx_ph: '例如 33.50',
  p_cdoc_fx_rate: '兑 {{cur}} 汇率 *',
  p_cdoc_issue_btn: '开具 {{docKind}}',
  p_cdoc_issue_date: '开具日期',
  p_cdoc_issuing: '开具中…',
  p_cdoc_items: '明细',
  p_cdoc_less_wht: '减:预扣税 {{amount}}',
  p_cdoc_net: '实收净额',
  p_cdoc_qty_title: '数量',
  p_cdoc_remark: '备注(选填)',
  p_cdoc_save_changes: '保存修改',
  p_cdoc_saving: '保存中…',
  p_cdoc_subtotal: '小计',
  p_cdoc_title_edit: '编辑 {{docKind}} {{docNo}}',
  p_cdoc_title_new: '新建{{docKind}}',
  p_cdoc_total: '合计',
  p_cdoc_type_invoice: '发票 / ใบแจ้งหนี้ — 服务类,税务发票于收款时开具',
  p_cdoc_type_proforma: '形式发票 —— 非税务发票,尚未产生 VAT 纳税义务',
  p_cdoc_type_tax_invoice: '税务发票/送货单 · ใบกำกับภาษี/ใบส่งของ — 货物,交货时计缴 VAT',
  p_cdoc_type_tir: '税务发票 / 收据 · ใบกำกับภาษี/ใบเสร็จรับเงิน — 现销,当场付清',
  p_cdoc_unitprice_title: '单价',
  p_cdoc_valid_until: '有效期至',
  p_cdoc_vat: 'VAT',
  p_cdoc_vat_title: 'VAT 处理方式',
  // crec
  p_crec_already_paid: '已付',
  p_crec_attach_hint: '之后可在收据列表中上传买方的 50 ทวิ 凭证。',
  p_crec_cash: '实收金额({{cur}})',
  p_crec_date_ok: '收据将按 {{yyyymm}} 计日期和编号',
  p_crec_date_required: '必填 — 以银行对账单为准,不是您录入的当天日期',
  p_crec_differs: '· 与未付金额 {{amount}} 不符 — 将记为部分付款',
  p_crec_err: '失败',
  p_crec_expected_wht: '预计预扣税',
  p_crec_invoice: '发票 *',
  p_crec_invoice_total: '发票总额',
  p_crec_issue: '开具收据',
  p_crec_option_outstanding: '未收 {{cur}} {{amount}}',
  p_crec_outstanding: '未收',
  p_crec_payment_date: '收款日期(款项到账日)*',
  p_crec_saving: '保存中…',
  p_crec_select_invoice: '— 选择未付发票 —',
  p_crec_settling: '结清 {{total}} = 现金 {{cash}} + 预扣税 {{wht}}',
  p_crec_subtitle: '选择发票后会自动带出尚未收到的金额,以及买方预计代扣的预扣税。若实际到账金额不同,可自行调整这两个数字。',
  p_crec_title: '登记收款',
  p_crec_wht: '买方代扣的预扣税',
  // cust
  p_cust_add: '+ 新增客户',
  p_cust_confirm_remove: '移除 {{name}}?',
  p_cust_empty: '暂无客户 — 添加第一位买方。',
  p_cust_f_address: '地址',
  p_cust_f_branch: '分支编号',
  p_cust_f_email: '邮箱',
  p_cust_f_name: '公司 / 个人名称 *',
  p_cust_f_name_th: '泰文名称(选填)',
  p_cust_f_notes: '备注',
  p_cust_f_notes_hint: '— 仅内部可见,不显示在单据上',
  p_cust_f_phone: '电话',
  p_cust_f_taxid: '税号',
  p_cust_modal_edit: '编辑客户',
  p_cust_modal_new: '新增客户',
  p_cust_search_ph: '搜索名称、编号、税号、邮箱…',
  p_cust_th_branch: '分支编号',
  p_cust_th_email: '邮箱',
  p_cust_th_name: '名称',
  p_cust_th_no: '编号',
  p_cust_th_taxid: '税号',
  p_cust_title: '我的客户',
  // disc
  p_disc_accept: '本人已阅读并接受上述内容。',
  p_disc_body_bold: '单据编号的正确性与连续性由您自行负责',
  p_disc_body_p1: '您的所有销售单据(报价单、发票、收据)都必须通过本系统开具,以保持编号连续。编号不会重复使用;单据作废后,其编号保留为 VOID。',
  p_disc_body_p2: ',包括您在本系统之外开具的单据。',
  p_disc_continue: '继续',
  p_disc_title: '单据编号由您负责',
  // doc
  p_doc_act_attach_wht: '上传 50 ทวิ',
  p_doc_act_attach_wht_title: '上传买方出具的 50 ทวิ 凭证',
  p_doc_act_delete: '删除并释放编号',
  p_doc_act_edit: '编辑',
  p_doc_act_pdf: 'PDF',
  p_doc_act_send: '发送至…',
  p_doc_act_send_title: '将此单据邮件发送给客户,并附 PDF',
  p_doc_act_void: '作废',
  p_doc_add_new: '+ 新建{{docKind}}',
  p_doc_add_note: '+ 新建折让单 / 增额单',
  p_doc_add_receipt: '+ 登记收款',
  p_doc_confirm_handed_over: '{{docNo}} 已被下载为 PDF,买方可能已持有该副本。\n\n修改后,对方手中的副本将与系统记录不一致。若对方已持有,请改为开具折让单/增额单,或作废后重新开具。\n\n仍要修改?',
  p_doc_delete_prompt: '删除 {{docNo}} 并释放该编号?\n\n仅同一系列中最新的编号可以释放。您下次开具的单据将重新使用 {{docNo}}。\n\n原因?(将被记录)',
  p_doc_empty: '暂无内容。',
  p_doc_empty_notes: '尚无折让单或增额单。可从需更正的税务发票开具。',
  p_doc_err_open: '无法打开此单据。',
  p_doc_freed: '{{docNo}} 已释放 — 下一份单据将使用该编号。',
  p_doc_head_invoices: '发票',
  p_doc_head_notes: '折让/增额单',
  p_doc_head_quotations: '报价单',
  p_doc_head_receipts: '收据',
  p_doc_kind_invoice: '发票',
  p_doc_kind_quotation: '报价单',
  p_doc_not_editable: '该单据不可编辑。',
  p_doc_note_credit: '折让',
  p_doc_note_debit: '增额',
  p_doc_reason_required: '必须填写原因。',
  p_doc_search_ph: '搜索编号、客户、地址…',
  p_doc_send_tooltip: '已发送 {{count}} 次 · 最近发往 {{email}}',
  p_doc_status_open: '未结',
  p_doc_status_paid: '已付款',
  p_doc_status_partial: '部分付款',
  p_doc_status_unpaid: '未付款',
  p_doc_status_void: 'VOID',
  p_doc_th_adjusts: '调整发票',
  p_doc_th_customer: '客户',
  p_doc_th_date: '日期',
  p_doc_th_difference: '差额({{cur}})',
  p_doc_th_no: '编号',
  p_doc_th_due: '\u5230\u671f\u65e5',
  p_doc_th_owing: '\u672a\u6536\u4f59\u989d',
  p_sum_n_inv: '{{count}} \u5f20\u53d1\u7968',
  p_sum_oldest: '\u6700\u4e45 {{days}} \u5929 \u2014 {{docNo}}',
  p_sum_outstanding: '\u5f85\u6536\u6b3e',
  p_sum_overdue: '\u5df2\u903e\u671f',
  p_sum_scope: '\u5168\u90e8\u671f\u95f4 \u00b7 \u622a\u81f3\u4eca\u65e5',
  p_doc_th_invoice_no: '对应发票',
  p_conv_already: '这张报价已经开过：{{list}}。再开一张是允许的（报价可以分期开票），但你的客户会收到第二张发票。',
  p_conv_cancel: '取消',
  p_conv_done: '发票 {{docNo}} 已开具。',
  p_conv_go: '开具发票',
  p_conv_loading: '正在检查这张报价…',
  p_conv_receipts: '已经挂在这张报价下的收款（{{list}}）不会转过去，所以新发票按全额未付开出。把它们搬过去会改写客户手上已经持有的单据。',
  p_conv_subtitle: '发票会逐行复制这张报价，日期为今天，初始状态为未付款。',
  p_conv_title: '由 {{docNo}} 开具发票',
  p_conv_type_invoice: '普通发票 Invoice',
  p_conv_type_invoice_hint: '帐单。服务业的销项税在客户付款时才发生（s.78/1），所以税票是后面那张收据。',
  p_conv_type_label: '这张开成哪种单据？',
  p_conv_type_locked: '开出后不能再改——它决定纳税义务什么时候发生。要改只能作废重开。',
  p_conv_type_proforma: '形式发票 Proforma',
  p_conv_type_proforma_hint: '不是税务单据。不产生纳税义务，也不入账。',
  p_conv_type_tax_invoice: '税务发票 Tax invoice',
  p_conv_type_tax_invoice_hint: '卖货的销项税在交货时发生（s.78），所以 VAT 记在这张单上——算在本月。',
  p_conv_working: '开具中…',
  p_doc_act_to_invoice: '开具发票',
  p_doc_link_self: '本单即收据',
  p_doc_th_property: '房产',
  p_doc_th_receipt_no: '对应收据',
  p_doc_th_received: '已收({{cur}})',
  p_doc_th_sent: '已发送',
  p_doc_th_status: '状态',
  p_doc_th_total: '合计',
  p_doc_th_wht: '预扣税',
  p_doc_void_prompt: '作废 {{docNo}}?\n\n该编号将以 VOID 状态保留在序列中,且不会重复使用。\n\n作废原因?(将被记录)',
  p_doc_wht_saved: '预扣税凭证已保存 ✓',
  // empty
  p_empty_no_match: '未找到与“{{query}}”匹配的结果。',
  // erec
  p_erec_bank: '单据上显示的银行账户',
  p_erec_bank_none: '— 无 —',
  p_erec_received: '已收 {{amount}}',
  p_erec_remark: '备注',
  p_erec_saving: '保存中…',
  p_erec_title: '编辑收据 {{docNo}}',
  p_erec_warning: '本收据同时是税务发票。客户、金额、VAT 和日期是买方申报进项税的依据,因此不能在此修改 — 如需更正其中任何一项,请作废本收据并重新登记该笔收款。',
  p_erec_wht: '· 预扣税 {{amount}}',
  // err
  p_err_create_failed: '创建失败',
  p_err_save_failed: '保存失败',
  // exp
  p_exp_act_remove: '移除',
  p_exp_act_rescan: '重新识别金额',
  p_exp_act_rescan_title: '重新识别该扫描件的金额',
  p_exp_act_view: '查看附件',
  p_exp_confirm_remove: '删除 {{filename}}?',
  p_exp_empty: '{{year}} 年 {{month}}尚未上传任何文件。',
  p_exp_err_open: '无法打开该文件',
  p_exp_file_fallback: '文件',
  p_exp_file_hint: '仅支持照片和 PDF — Word 和 Excel 无法读取,请先导出为 PDF。多页发票请合并为一个 PDF,不要分开拍照。',
  p_exp_filed_warn: '{{year}} 年 {{month}} 的 VAT 申报已提交。本月之后再发送的资料将按增补申报处理,不计入已提交的那份申报表。',
  p_exp_filed_warn_bold: '增补申报',
  p_exp_filter_all: '全部状态',
  p_exp_filter_approved: '已入账',
  p_exp_filter_pending: '等待 {{brand}} 处理',
  p_exp_filter_rejected: '已退回',
  p_exp_list_title: '已上传 — {{year}} 年 {{month}}',
  p_exp_month: '月份',
  p_exp_msg_appended: '· {{filename}} — 已作为发票 {{docNo}} 的第 {{page}} 页添加',
  p_exp_msg_ocr_error: '部分文件无法自动识别 — {{brand}} 将手工录入金额。',
  p_exp_msg_saved: '已收到 {{n}} 张发票,等待 {{brand}} 处理。',
  p_exp_msg_skipped: '{{n}} 份因重复被跳过:',
  p_exp_msg_skipped_line: '· {{filename}}{{docNo}} —— {{why}}',
  p_exp_no_vat: '无 VAT',
  p_exp_ocr_error_row: '无法自动识别',
  p_exp_page_chip: 'p{{n}}',
  p_exp_page_title: '第 {{n}} 页',
  p_exp_quota_unlimited: '{{year}} 年 {{month}}:已上传 {{used}} 份 · 不限数量',
  p_exp_quota_full: '{{year}} 年 {{month}} 已达每月 {{cap}} 份上限 — 需要更多请邮件联系 {{contact}}',
  p_exp_quota_left: '{{month}} {{year}}:已用 {{used}}/{{cap}} · 剩余 {{left}}',
  p_exp_reading: '识别中…',
  p_exp_search_ph: '搜索供应商、发票号、文件…',
  p_exp_status_approved: '已入账',
  p_exp_status_pending: '待 {{brand}} 处理',
  p_exp_status_rejected: '已退回',
  p_exp_subtitle: '您已支付账单的照片或 PDF。{{brand}} 会逐份与扫描件核对后再入账 — 这里上传的内容本身不会改变您的报税。',
  p_exp_th_amount: '金额',
  p_exp_th_attachment: '附件',
  p_exp_th_bucket: '分类',
  p_exp_th_date: '日期',
  p_exp_th_docno: '发票编号',
  p_exp_th_status: '状态',
  p_exp_th_supplier: '供应商',
  p_exp_th_vat: 'VAT',
  p_exp_title: '请上传您的采购发票',
  p_exp_upload: '＋ 上传发票',
  p_exp_upload_failed: '上传失败',
  p_exp_with_vat: '含 VAT',
  p_exp_year: '年份',
  // fil
  p_fil_chip_additional: 'A{{n}}',
  p_fil_chip_attachment: '附件',
  p_fil_chip_form: '申报表',
  p_fil_chip_receipt: '缴税收据',
  p_fil_legend: '绿色 = 已申报,下方可查看文件 · 申报表 = 已提交的申报表 · 收据 = 税务局缴款收据 · ✓ = 已申报,文件尚未上传。缺少内容?联系 {{brand}},我们会补上。',
  p_fil_loading: '加载中…',
  p_fil_subtitle: '{{brand}} 为您逐月申报的税表。点击月份可查看已申报表单或税务局回执。',
  p_fil_th_filing: '申报表',
  p_fil_title: '税务与申报',
  // filing
  p_filing_doc_gone: '该单据已不可用,请联系 {{brand}}。',
  // img
  p_img_err_generic: '无法读取该图片',
  p_img_err_not_image: '该文件不是图片',
  p_img_err_read: '无法读取该文件',
  p_img_none: '无',
  p_img_remove: '移除',
  p_img_replace: '替换',
  p_img_upload: '上传',
  // imp
  p_imp_already_bold: '{{n}} 个单据编号已存在',
  p_imp_already_rest: ',导入将中止:{{list}}',
  p_imp_btn: '导入',
  p_imp_btn_n: '导入 {{n}} 条',
  p_imp_confirm: '按表格中的单据编号创建 {{n}} 份{{docTypeWord}}?\n\n将新建 {{m}} 条客户记录。\n\n此操作无法批量撤销 — 只能逐份作废单据。',
  p_imp_create_as: '创建为',
  p_imp_done: '✓ 已创建 {{created}} 张发票 · 新增 {{customers}} 位客户',
  p_imp_done_btn: '完成',
  p_imp_err_failed: '导入失败',
  p_imp_err_read: '无法读取该文件。',
  p_imp_open: '从表格导入',
  p_imp_map_title: '把表格的列对应到系统字段',
  p_imp_map_saved: '· 已套用你上次为这种表保存的对应关系',
  p_imp_map_edit: '修改对应',
  p_imp_map_hide: '收起对应',
  p_imp_map_th_column: '表格列',
  p_imp_map_th_sample: '示例内容',
  p_imp_map_th_field: '对应到',
  p_imp_map_th_vat: 'VAT 口径',
  p_imp_map_th_label: '发票行名称',
  p_imp_map_no_header: '第 {{n}} 列(无表头)',
  p_imp_map_required: '没有任何列对应到「{{field}}」—— 每张单据都必须有。',
  p_imp_map_need_amount: '至少要指定一列金额 —— 它才是发票的行项。',
  p_imp_field_ignore: '不导入',
  p_imp_field_customer: '客户名称',
  p_imp_field_docNo: '发票编号',
  p_imp_field_issueDate: '开票日期',
  p_imp_field_dueDate: '到期日',
  p_imp_field_address: '单据上的地址',
  p_imp_field_description: '说明',
  p_imp_field_remark: '备注',
  p_imp_field_fee: '金额(生成一行)',
  p_imp_field_total: '表格自带合计(仅用于核对)',
  p_imp_fee_q: '各费用列如何计税?',
  p_imp_fee_warn: '—— 此处填错,每位买方的付款金额都会差 7%',
  p_imp_file_label: '电子表格 (.xlsx)',
  p_imp_header_incl: '· 表头标注“含 VAT”',
  p_imp_header_unknown: '· 表头未说明 — 请确认',
  p_imp_mismatch_body: '发票将按上方的费用列开具;表中合计的口径可能不同。导入前请抽查几笔:',
  p_imp_mismatch_bold: '有 {{n}} 行与表格自身的合计列对不上。',
  p_imp_mismatch_line: '第 {{n}} 行 · {{docNo}} · 发票 {{a}},表格 {{b}}',
  p_imp_more: '… 另有 {{n}} 条',
  p_imp_prev_th_customer: '客户',
  p_imp_prev_th_date: '日期',
  p_imp_prev_th_lines: '明细行',
  p_imp_prev_th_total: '合计',
  p_imp_sheet: '工作表',
  p_imp_skipped_head: '已跳过 — 以下不会导入',
  p_imp_skipped_row: '第 {{n}} 行',
  p_imp_skipped_title: '已跳过的行 — 未为其创建任何单据:',
  p_imp_subtitle: '每张发票一行。单据编号、日期和金额完全按您的文件原样导入,不作生成或调整。',
  p_imp_summary: '将创建 {{rows}} 张发票 · 新增客户 {{customers}} 家 · 合计 {{amount}} {{cur}}',
  p_imp_summary_skipped: '跳过 {{skipped}} 行',
  p_imp_th_docno: '发票号',
  p_imp_th_owner: '所属人',
  p_imp_th_property: '房产',
  p_imp_th_row: '行',
  p_imp_th_why: '原因',
  p_imp_title: '从表格导入发票',
  p_imp_type_inv: '发票 — 仅用于收款,税务发票于付款时开具',
  p_imp_type_ti: '税务发票 / 送货单 — 交付时产生 VAT',
  p_imp_type_tir: '税务发票 / 收据 —— 现销,已结清',
  p_imp_word_invoices: '发票',
  p_imp_word_tax_invoices: '税务发票',
  p_imp_working: '处理中…',
  // lang
  p_lang_en: 'English',
  p_lang_en_short: 'EN',
  p_lang_th_short: 'ไทย',
  p_lang_zh_short: '中',
  // loading
  p_loading: '加载中…',
  // login
  p_login_email: '邮箱',
  p_login_err_failed: '登录失败',
  p_login_err_staff: '这里是客户门户,员工请使用内部系统。',
  // Cross-installation sign-in (owner 2026-10-15)
  p_login_err_blank: '请输入邮箱和密码。',
  p_login_hop_text: '该账号属于事务所的另一处安装({{host}}),要在那里登录吗?',
  p_login_hop_go: '是,去那里登录',
  p_login_hop_cancel: '取消',
  p_login_password: '密码',
  p_login_signin: '登录',
  p_login_signing_in: '登录中…',
  p_login_subtitle: '面向企业的开票与账单',
  p_login_title: '{{brand}} 客户门户',
  // logo
  p_logo_alt: '{{brand}}',
  // menu
  p_menu_actions_tooltip: '操作',
  p_menu_bills: '{{brand}} 的账单',
  p_menu_bills_hint: '{{brand}} 向您收取的费用',
  p_menu_customers: '客户',
  p_menu_customers_hint: '您开票的买方',
  p_menu_filings: '税务与申报',
  p_menu_filings_hint: '{{brand}} 为您申报的税表',
  p_menu_group_fromslf: '来自 {{brand}}',
  p_menu_group_mydata: '我的资料',
  p_menu_group_company: '公司',
  p_menu_products: '商品与服务',
  p_menu_products_hint: '您销售的商品/服务',
  p_menu_profile: '公司资料',
  p_menu_profile_hint: '地址、标志、单据编号',
  p_menu_tooltip: '菜单',
  p_menu_working: '处理中…',
  // month
  p_month_apr: '4月',
  p_month_aug: '8月',
  p_month_dec: '12月',
  p_month_feb: '2月',
  p_month_jan: '1月',
  p_month_jul: '7月',
  p_month_jun: '6月',
  p_month_mar: '3月',
  p_month_may: '5月',
  p_month_nov: '11月',
  p_month_oct: '10月',
  p_month_sep: '9月',
  // nav
  p_nav_bankstmt: '银行流水',
  p_nav_expenses: '采购发票',
  p_office_label: '您的 {{brand}} 办公室',
  p_office_hint: '为您记账并出具单据的 {{brand}} 办公室',
  p_nav_owner: '老板控制台',
  p_own_title: '老板控制台',
  p_own_lede: '一眼看清:谁欠你钱、你欠多少、接下来要交什么。',
  p_own_empty: '这里暂时还没有内容。等你开始开票、或我们开始为你申报,这一页就会有数据。',
  p_own_owed_you: '别人欠你',
  p_own_overdue_suffix: '已逾期',
  p_own_chase: '最久未收',
  p_own_days_late: '逾期 {{days}} 天',
  p_own_owed_slf: '你欠 {{brand}}',
  p_own_next_due: '最近一笔 {{date}} 到期',
  p_own_waiting: '等你处理',
  p_own_and_more: '另有 {{n}} 项',
  p_own_task_upload: '把本月单据发给我们',
  p_own_task_tax_pay: '缴税',
  p_own_task_svc_pay: '支付我们的服务费',
  p_own_task_info: '补充我们需要的资料',
  p_own_upcoming: '接下来要交',
  p_own_today: '今天',
  p_own_in_days: '{{days}} 天后',
  p_own_amount_tbc: '金额待定',
  p_own_amount_credit: '留抵 {{amount}}',
  p_own_efiling_note: '以上是税务局电子申报的截止日,比柜台申报日要晚。',
  p_own_booked: '{{year}} 年已入账',
  p_own_revenue: '销售额',
  p_own_purchases: '采购额',
  p_own_booked_note: '来自已记录的 {{months}} 个月。这不是利润——工资要等当月结账后才计入。',
  p_own_filed: '{{year}} 年已为你申报',
  p_own_filed_note: '我们已提交的申报表',
  p_nav_books: '我的账簿',
  p_menu_company_find: '查找公司…',
  p_menu_books_locked: '开账后可见',
  p_books_period: '会计期间',
  p_menu_company_none: '没有匹配的公司。',
  p_menu_company_n_of: '{{n}} / {{total}}',
  p_menu_policy: '我们怎么记你的账',
  p_menu_policy_hint: '我们对你账务实际采用的处理',
  p_menu_chart: '科目表',
  p_menu_chart_hint: '你的账记在哪些科目里',
  p_menu_ledgers: '账本与试算表',
  p_menu_ledgers_hint: '五本日记账、分类账与试算表',
  p_books_no_entries: '本期无分录',
  p_menu_statements: '财务报表',
  p_menu_statements_hint: '资产负债表、损益表、权益变动表',
  p_pol_title: '我们怎么记你的账',
  p_pol_lede: '这一页说明我们系统对你账务实际的处理方式。它描述的是软件正在执行的规则,不是法律条文的转述;涉及法律的部分,以你的会计师出具的书面意见为准。',
  p_pol_readonly: '本页为只读。',
  p_pol_s1_t: '什么时候算你的收入',
  p_pol_s1_b: '开税务发票(含税务发票/收据):在单据开立日当月计入销项。开普通发票:要等收到钱、并登记了收据之后才计入。这意味着一张普通发票如果本月没收到钱,这笔收入不会出现在本月的申报基数里;如果一直没收到,就一直不会出现。',
  p_pol_s2_t: '预扣税什么时候记',
  p_pol_s2_b: '客户代扣的预扣税,我们在收到款时才记,不在开票当期。所以你看到的应收挂的是全额,不是你最终到账的金额。',
  p_pol_s3_t: '折让单与增额单',
  p_pol_s3_b: '发票开出去之后要改金额,我们另开折让单或增额单,在它自己开立的月份单独入账。原来那张发票的记录不会被改动——这是审计要看的轨迹。',
  p_pol_s4_t: '外币',
  p_pol_s4_b: '我们系统里不保存任何汇率。开外币单据时,汇率由开单的人逐张录入;年末换算所需的汇率也必须由人提供给我们。没有汇率我们就拒绝换算,不会估一个数字出来。外币应收因此按币种分别显示,不折算成$t(cur_word)合计。',
  p_pol_s5_t: '不能抵扣的进项税',
  p_pol_s5_b: '不能抵扣的进项税,我们计入费用科目 5710,不留在可退税的进项里。',
  p_pol_s6_t: '存货计价',
  p_pol_s6_b: '我们支持两种成本法:先进先出与加权平均。一旦某一期用某个方法估过值,就不能再换成另一种。当存货记录对不上时——有出库没有对应的入库,或者出得比进得多——我们拒绝出计价结果,不会给一个没有依据的数字。',
  p_pol_s7_t: '折旧',
  p_pol_s7_b: '折旧按直线法计算,并按你在该期间内实际持有资产的天数按比例分摊(一年按 365 天)。折旧不会把资产计到残值以下,也不会自己过账:每一笔都先生成草稿,经人工复核后才进入你的账。我们采用的上限如下。',
  p_pol_s7_note: '表中为上限税率。某项资产若未单独设定税率,则按其类别的上限计提。',
  p_pol_s8_t: '亏损结转',
  p_pol_s8_b: '我们逐年登记你的可结转亏损。到期年度一栏印的是「未确定」,因为我们没有可引用的依据来源。要填这个年限,必须同时记录它的法律出处。',
  p_pol_s9_t: '单据到了我们这里会怎样',
  p_pol_s9_b: '你发来的每一份单据归为三类之一。它先变成一张草稿分录,由人工复核后才过账。过账之后的分录永不删除——有错就用一张可见的冲回分录更正。每一笔分录落在五本日记账之一;银行类的分录必须指明是哪个账户。',
  p_pol_th_class: '资产类别',
  p_pol_th_rate: '上限税率',
  p_pol_not_dep: '不折旧',
  p_books_none_t: '你的账还没有建起来',
  p_books_none_b: '等我们为你开好会计期间、过了第一批分录,你的科目表、账本与财务报表就会出现在这里。',
  p_nav_invoices: '发票',
  p_nav_notes: '折让单 / 增额单',
  p_nav_quotations: '报价单',
  p_nav_receipts: '收据',
  p_nav_sign_out: '退出登录',
  // note
  p_note_amount_ph: '金额',
  p_note_difference_label: '差额',
  p_note_err: '无法开具此单据',
  p_note_info: '只填写本单调整的差额,而非原单据全额。该差额对应的 VAT 计入本单开具当月的销项税。',
  p_note_issue_btn: '开具单据',
  p_note_issue_date: '开具日期',
  p_note_issuing: '开具中…',
  p_note_item_ph: '调整的内容',
  p_note_kind_credit: '折让单(ใบลดหนี้)',
  p_note_kind_debit: '增额单 (ใบเพิ่มหนี้)',
  p_note_no_sources: '尚无税务发票 —— 折让/增额单只能调整含 VAT 的单据。',
  p_note_opt_credit: '折让单 —— 冲减金额(ใบลดหนี้)',
  p_note_opt_debit: '增额单 — 增加金额 (ใบเพิ่มหนี้)',
  p_note_reason_discount: '开票后给予折扣 (ลดราคาภายหลัง)',
  p_note_reason_extra_goods: '追加交付货物或服务(ส่งมอบเพิ่มเติม)',
  p_note_reason_goods_defective: '货物瑕疵 / 与订单不符(สินค้าชำรุดบกพร่อง)',
  p_note_reason_goods_returned: '货物退回(สินค้าถูกส่งคืน)',
  p_note_reason_label: '原因 *(打印在单据上)',
  p_note_reason_other: '其他(อื่น ๆ)',
  p_note_reason_other_ph: '请说明原因',
  p_note_reason_overcharge: '原单据计价高于实际(คำนวณราคาสูงกว่าที่เป็นจริง)',
  p_note_reason_service_not_provided: '服务部分或全部未提供(บอกเลิกการให้บริการ)',
  p_note_reason_undercharge: '原单据金额低于实际 (คำนวณราคาต่ำกว่าที่เป็นจริง)',
  p_note_ref: '被调整的税务发票 *',
  p_note_title_new: '新建{{noteKind}}',
  p_note_total_difference: '差额',
  p_note_type: '类型 *',
  // pager
  p_pager_first: '首页',
  p_pager_last: '末页',
  p_pager_next: '下一页',
  p_pager_no_records: '无记录',
  p_pager_page_of: '第 {{page}} 页 / 共 {{pages}}',
  p_pager_per_page: '每页',
  p_pager_prev: '上一页',
  p_pager_showing: '显示 {{from}}–{{to}} / 共 {{total}}',
  // pdf
  p_pdf_failed: 'PDF 生成失败',
  // prod
  p_prod_add: '+ 添加项目',
  p_prod_confirm_remove: '确定删除 {{name}}?',
  p_prod_empty: '暂无项目,请添加您销售的商品或服务。',
  p_prod_f_name: '名称 *',
  p_prod_f_price: '单价({{cur}})',
  p_prod_f_unit: '单位(如 个、小时)',
  p_prod_f_vat_checkbox: '适用 7% VAT',
  p_prod_f_wht: '默认预扣税(WHT)',
  p_prod_f_wht_hint: '商品通常 0%;服务 3%;租金 5%。买方代扣后会向您出具 50 ทวิ 凭证。',
  p_prod_modal_edit: '编辑项目',
  p_prod_modal_new: '新增项目',
  p_prod_photo_hint: '显示在商品目录中',
  p_prod_photo_label: '图片(选填)',
  p_prod_search_ph: '搜索名称或单位…',
  p_prod_th_name: '名称',
  p_prod_th_price: '价格({{cur}})',
  p_prod_th_unit: '单位',
  p_prod_th_vat: 'VAT',
  p_prod_title: '我的商品与服务',
  p_prod_vat_yes: '{{rate}}%',
  // prof
  p_prof_address: '单据上的地址',
  p_prof_biz_goods: '货物 — 交付时即产生 VAT 纳税义务,税务发票随货开具 (s.78)',
  p_prof_biz_hint: '此项仅决定新建单据时的默认类型,每份单据仍可单独更改。',
  p_prof_biz_mixed: '商品与服务兼营',
  p_prof_biz_service: '服务业 —— 客户付款时产生 VAT 纳税义务(s.78/1)',
  p_prof_biz_type: '经营类型',
  p_prof_company_name: '公司名称(锁定)',
  p_prof_default_wht: '新明细行默认预扣税',
  p_prof_default_wht_hint: '当明细行未选目录项目时适用。',
  p_prof_doc_cn: '折让单',
  p_prof_doc_dn: '增额单',
  p_prof_doc_inv: '发票',
  p_prof_doc_pro: '形式发票',
  p_prof_doc_quo: '报价单',
  p_prof_doc_rec: '收据',
  p_prof_doc_tax: '税务发票',
  p_prof_email: '邮箱',
  p_prof_failed: '失败',
  p_prof_identity_note: '身份信息依据贵公司文件核验,仅可由我方团队修改。',
  p_prof_logo_hint: '打印在发票、报价单和收据左上角',
  p_prof_logo_label: '公司 Logo',
  p_prof_month_seg: '单据编号中包含年月',
  p_prof_month_seg_hint: '— 开启:INV-{{yyyymm}}-001 · 关闭:INV-001。如果您自己的编号已包含年月,请关闭此项。',
  p_prof_numbering: '单据编号',
  p_prof_numbering_note: '— 编号连续性由您负责(已于 {{date}} 确认)',
  p_prof_phone: '电话',
  p_prof_prefix_next: '{{docType}} 前缀 / 下一号码',
  p_prof_pw_changed: '密码已修改 ✓',
  p_prof_saved: '已保存 ✓',
  p_prof_branch: '分支机构(不可修改)',
  p_prof_branch_head: '总公司',
  p_prof_branch_hint: '开具税务发票、折让单、增额单时，打在税号旁边。',
  p_prof_branch_n: '分支机构',
  p_prof_tax_id: '税号(不可修改)',
  p_prof_title: '公司资料',
  p_prof_vat_hint: '已登记 ⇒ 发票加收 7% VAT,收据即为税务发票。',
  p_prof_vat_not_registered: '未登记',
  p_prof_vat_reg: 'VAT 登记',
  p_prof_vat_registered: '已登记(7% VAT)',
  p_prof_website: '网站',
  // pw
  p_pw_confirm: '确认新密码',
  p_pw_current: '当前密码',
  p_pw_err_current_required: '请输入当前密码。',
  p_pw_err_failed: '失败',
  p_pw_err_min_length: '至少 8 位字符。',
  p_pw_err_mismatch: '两次输入的密码不一致。',
  p_pw_hint_current: '需填写当前密码。',
  p_pw_hint_first: '为安全起见,请更换临时密码。',
  p_pw_new: '新密码',
  p_pw_title_change: '修改密码',
  p_pw_title_first: '设置您的密码',
  // row
  p_row_delete: '删除',
  p_row_edit: '编辑',
  // search
  p_search_clear: '清除',
  // select
  p_select_placeholder: '— 请选择 —',
  p_pick_search: '输入以搜索…',
  p_pick_none: '没有匹配 — 换个关键词试试。',
  p_pick_more: '显示 {{shown}} / 共 {{total}} — 输入关键词可搜索其余。',
  p_cust_add_title: '新增客户',
  p_cust_add_save: '新增并选中',
  p_cust_name_req: '客户名称必填。',
  p_cust_add_failed: '新增客户失败。',
  p_prof_f_prefix: '前缀',
  p_prof_f_next: '下一个号',
  p_prof_f_date: '编号中的日期',
  p_prof_f_reset: '重新开始',
  p_prof_f_pad: '位数',
  p_prof_date_none: '不含日期',
  p_prof_reset_none: '不重置',
  p_prof_reset_year: '每年',
  p_prof_reset_month: '每月',
  p_prof_reset_day: '每日',
  p_prof_scheme_bad: '编号里必须含 {{need}},否则下个周期会出现重复编号。',
  p_prof_numbering_hint: '编号 = 前缀 · 日期 · 流水号。要让流水号重新开始,编号里的日期必须能区分周期:每年需含 YYYY,每月需含 YYYYMM,每日需含 YYYYMMDD。改动只影响此后新开的单据,已开出的单据编号一律不变。',
  p_flt_all: '全部',
  p_flt_custom: '自定义',
  p_flt_clear: '清除日期筛选',
  p_flt_period: '期间',
  p_flt_show_voided: '显示已作废',
  p_imp_open_short: '导入',
  p_menu_notes_hint: '更正已开出的单据',
  p_flt_this_month: '本月',
  p_flt_last_month: '上月',
  p_flt_from: '起始日期',
  p_flt_to: '结束日期',
  // send
  p_send_attachment: '附件',
  p_send_bcc: '抄送给您',
  p_send_btn: '发送邮件',
  p_send_done: '完成',
  p_send_err_prepare: '无法准备邮件。',
  p_send_err_send: '邮件发送失败。',
  p_send_from: '发件人',
  p_send_history: '发送记录',
  p_send_message: '正文',
  p_send_no_replyto: '— 请先在「公司资料」中填写公司邮箱 —',
  p_send_preparing: '准备中…',
  p_send_preview: '预览',
  p_send_preview_hint: '打开客户实际将收到的 PDF — 正本与副本两联。',
  p_send_replyto: '回复至',
  p_send_save_addr: '将此邮箱保存到 {{customerName}},下次自动填入',
  p_send_sending: '发送中…',
  p_send_sent_ok: '✓ 已发送至 {{email}},副本已存入您的收件箱。',
  p_send_subject: '主题',
  p_send_subtitle: 'PDF 会自动作为附件发送。客户的回复将直接回到您这里。',
  p_send_title: '发送 {{docNo}}',
  p_send_to_label: '客户邮箱 *',
  p_send_to_ph: 'name@example.com',
  // sort
  p_sort_th_tooltip: '按 {{label}} 排序',
  // vat
  p_vat_hint_exempt: '免征 VAT 业务(s.81)。',
  p_vat_hint_none: '不属 VAT 范围 / 非 VAT 登记人。',
  p_vat_hint_standard: 'VAT 在您填写的价格之外另加。',
  p_vat_hint_standard_incl: '所填价格已含 VAT。',
  p_vat_hint_zero: '零税率(s.80/1)—— 按 0% 计税,与免税不同。',
  p_vat_opt_exempt: 'VAT 免征',
  p_vat_opt_none: '无 VAT',
  p_vat_opt_standard: 'VAT {{rate}}% — 价外',
  p_vat_opt_standard_incl: 'VAT {{rate}}% — 价内含税',
  p_vat_opt_zero: 'VAT 0%',
  // wht
  p_wht_none: '无预扣税',
  p_wht_rate: '预扣税 {{rate}}%',
  p_wht_select_title: '买方代扣的预扣税',
};

export const portalTh: Record<string, string> = {
  // admin
  p_admin_title: '{{brand}} Client Portal — ผู้ดูแลระบบ',
  // bank
  p_bank_acct_name: 'ชื่อบัญชี',
  p_bank_acct_no: 'เลขที่บัญชี',
  p_bank_add: '+ เพิ่มบัญชีธนาคาร',
  p_bank_address: 'ที่อยู่ธนาคาร',
  p_bank_address_hint: '— จำเป็นสำหรับการโอนเงินจากต่างประเทศ',
  p_bank_address_ph: '1 Bangkok Blvd, Pathum Wan, Bangkok 10330, Thailand',
  p_bank_confirm_remove: 'ลบบัญชีธนาคารนี้หรือไม่?',
  p_bank_currency: 'สกุลเงิน',
  p_bank_empty: 'ยังไม่มีบัญชีธนาคาร — เพิ่มบัญชีเพื่อให้พิมพ์ลงบนเอกสารของท่าน',
  p_bank_name: 'ธนาคารและสาขา',
  p_bank_name_ph: 'Kasikornbank (KBank), One Bangkok Branch',
  p_bank_no_swift: '⚠ ไม่มี SWIFT / ที่อยู่ธนาคาร — ผู้ชำระเงินจากต่างประเทศจะโอนเข้าบัญชีนี้ไม่ได้',
  p_bank_note: 'หมายเหตุ (ไม่บังคับ)',
  p_bank_note_ph: 'เช่น ใช้กับใบแจ้งหนี้สกุล USD เท่านั้น',
  p_bank_qr_alt: 'QR',
  p_bank_qr_hint: 'PromptPay / QR ธนาคาร — พิมพ์ในช่องการชำระเงินบนใบแจ้งหนี้และใบเสนอราคา',
  p_bank_qr_label: 'QR รับชำระเงิน',
  p_bank_qr_title: 'QR รับชำระเงินที่พิมพ์บนเอกสาร',
  p_bank_swift: 'SWIFT / BIC',
  p_bank_swift_ph: 'KASITHBK',
  p_bank_title: 'บัญชีธนาคารที่แสดงบนเอกสาร',
  // bills
  p_bills_loading: 'กำลังโหลด…',
  p_bills_open_pdf: 'เปิด PDF',
  p_bills_subtitle: 'ใบเสนอราคา ใบแจ้งหนี้ และใบเสร็จรับเงินที่ {{brand}} ออกให้ท่าน · เอกสารที่ {{brand}} ออกให้ท่าน',
  p_bills_tab_invoices: 'ใบแจ้งหนี้',
  p_bills_tab_quotations: 'ใบเสนอราคา',
  p_bills_tab_receipts: 'ใบเสร็จรับเงิน',
  p_bills_th_date: 'วันที่',
  p_bills_th_due: 'ครบกำหนด',
  p_bills_th_issued_by: 'ออกโดย',
  p_bills_th_net: 'ยอดสุทธิ',
  p_bills_th_no: 'เลขที่',
  p_bills_th_paid_on: 'วันที่ชำระ',
  p_bills_th_status: 'สถานะ',
  p_bills_th_total: 'รวม ($t(cur_word))',
  p_bills_th_valid_until: 'ยืนราคาถึง',
  p_bills_title: 'ใบวางบิลจาก {{brand}}',
  p_menu_group_settings: 'ตั้งค่า',
  p_menu_files: 'เอกสาร', p_menu_files_hint: 'เอกสารทั้งหมดที่เราเก็บไว้ให้ท่าน รวมอยู่ที่เดียว',
  p_files_title: 'เอกสารของท่าน',
  p_files_subtitle: 'เอกสารทั้งหมดที่ {{brand}} เก็บไว้ให้ท่าน — แบบที่ยื่น ใบเสร็จกรมสรรพากร เอกสารบริษัท และเอกสารที่ท่านส่งมา',
  p_files_loading: 'กำลังโหลด…', p_files_none: 'ยังไม่มีเอกสาร', p_files_all: 'ทั้งหมด',
  p_files_search: 'ค้นหาเอกสาร…',
  p_files_name: 'เอกสาร', p_files_folder: 'ประเภท', p_files_size: 'ขนาด', p_files_added: 'เพิ่มเมื่อ',
  p_files_view: 'ดู', p_files_download: 'ดาวน์โหลด',
  p_files_f_tax: 'ภาษีและการยื่น', p_files_f_company: 'เอกสารบริษัท', p_files_f_uploads: 'ท่านส่งให้เรา',
  p_files_f_deliverables: 'ส่งมอบให้ท่าน', p_files_f_workflow: 'เอกสารระหว่างบริการ', p_files_f_library: 'อื่น ๆ',
  p_files_elsewhere: 'ใบแจ้งหนี้และใบเสร็จจาก {{brand}} อยู่ในเมนูใบวางบิลจาก {{brand}} ส่วนแบบที่ยื่นดูได้ที่การยื่นภาษีเช่นกัน',
  p_nav_finance: 'กำไรขาดทุน',
  p_fin_title: 'งบกำไรขาดทุน {{year}}',
  p_fin_subtitle: 'จากข้อมูลที่บันทึกจริงในบัญชีของท่าน ไม่มีการประมาณการและไม่มีการคาดการณ์ล่วงหน้า',
  p_fin_loading: 'กำลังโหลด…',
  p_fin_no_data: 'ยังไม่มีข้อมูลบันทึกสำหรับปีนี้',
  p_fin_revenue: 'รายได้',
  p_fin_expenses: 'ค่าใช้จ่าย',
  p_fin_purchases: 'ซื้อ',
  p_fin_salaries: 'ค่าจ้าง',
  p_fin_sso: 'ประกันสังคม (ส่วนนายจ้าง)',
  p_fin_pnd3: 'ค่าบริการจ่ายให้บุคคลธรรมดา',
  p_fin_rent53: 'ค่าเช่า',
  p_fin_expenses_total: 'รวมค่าใช้จ่าย',
  p_fin_net: 'สุทธิ',
  p_fin_by_month: 'แยกรายเดือน',
  p_fin_month: 'เดือน',
  p_fin_no_payroll: 'ยังไม่ได้จัดทำเงินเดือน',
  p_fin_incomplete: 'ยังไม่ได้จัดทำเงินเดือนของเดือน {{months}} ค่าจ้างและประกันสังคมจึงแสดงเป็นศูนย์ ตัวเลขเดือนเหล่านี้ยังไม่ใช่ตัวเลขสุดท้าย',
  p_fin_note: 'รายได้และยอดซื้อเป็นจำนวนก่อน VAT ประกันสังคมแสดงเฉพาะส่วนของนายจ้าง ส่วนของพนักงานหักจากเงินเดือนและไม่ใช่ต้นทุนเพิ่มของบริษัท ค่าบริการที่จ่ายให้นิติบุคคลรวมอยู่ในยอดซื้อแล้ว จึงแยกแสดงเฉพาะที่จ่ายให้บุคคลธรรมดา',
  p_nav_calendar: 'ปฏิทิน',
  p_cal_title: 'สิ่งที่กำลังจะถึงกำหนด',
  p_cal_subtitle: 'กำหนดยื่นภาษีของท่าน และงานของทั้งสองฝ่าย รายการที่ระบุว่า "ท่าน" คือรอท่านดำเนินการ',
  p_cal_loading: 'กำลังโหลด…', p_cal_nothing: 'ไม่มีรายการในช่วงนี้',
  p_cal_all_year: 'ทั้งปี', p_cal_today: 'วันนี้', p_cal_you: 'ท่าน',
  p_cal_only_mine: 'เฉพาะของท่าน ({{count}})', p_cal_yours_count: 'รอท่าน {{count}} รายการ',
  p_cal_is_filing: 'กำหนดยื่น', p_cal_done: 'เสร็จแล้ว', p_cal_blocked: 'ติดขัด',
  p_cal_k_pp30: 'ภาษีมูลค่าเพิ่ม (ภ.พ.30)', p_cal_k_pnd1: 'ภ.ง.ด.1', p_cal_k_pnd3: 'ภ.ง.ด.3', p_cal_k_pnd53: 'ภ.ง.ด.53',
  p_cal_k_sso: 'ประกันสังคม', p_cal_k_pnd51: 'ภ.ง.ด.51 (ครึ่งปี)', p_cal_k_pnd50: 'ภ.ง.ด.50 (ประจำปี)',
  p_cal_k_pnd90: 'ภาษีเงินได้บุคคลธรรมดา', p_cal_k_audit: 'ตรวจสอบบัญชี', p_cal_k_annual_fs: 'งบการเงินประจำปี',
  p_cal_k_bookkeeping: 'ทำบัญชี', p_cal_k_property_tax: 'ภาษีที่ดินและสิ่งปลูกสร้าง',
  p_cal_k_invoice_data_upload: 'ส่งใบกำกับภาษีให้เรา', p_cal_k_tax_payment: 'ชำระภาษี',
  p_cal_k_service_payment: 'ชำระค่าบริการ', p_cal_k_service_invoice: 'ใบแจ้งหนี้จากเรา',
  p_cal_k_tax_remit: 'เรานำส่งภาษีให้ท่าน', p_cal_k_lead_quotation: 'ใบเสนอราคา',
  p_cal_k_lead_contract: 'สัญญา', p_cal_k_lead_collect_info: 'ส่งข้อมูลให้เรา',
  p_doc_got_bill: 'ได้รับใบแจ้งหนี้จากผู้ขายหรือไม่',
  p_doc_got_receipt: 'มีใบเสร็จจากที่ท่านชำระไปหรือไม่',
  p_doc_send_to_slf: 'ส่งให้เราอ่านและบันทึกบัญชี →',
  p_nav_hr: 'พนักงาน',
  p_hr_title: 'พนักงานของท่าน',
  p_hr_subtitle: 'รายชื่อพนักงานและเงินสมทบประกันสังคม หากต้องการแก้ไข โปรดส่งคำขอมา เราจะดำเนินการให้',
  p_hr_loading: 'กำลังโหลด…',
  p_hr_new_request: 'ส่งคำขอแก้ไข',
  p_hr_pending_note: 'มีคำขอ {{count}} รายการรอ {{brand}} ตรวจสอบ',
  p_hr_name: 'ชื่อ', p_hr_position: 'ตำแหน่ง', p_hr_salary: 'เงินเดือน',
  p_hr_joined: 'เริ่มงาน', p_hr_status: 'สถานะ', p_hr_nationality: 'สัญชาติ',
  p_hr_no_staff: 'ยังไม่มีข้อมูลพนักงาน',
  p_hr_active: 'ทำงานอยู่', p_hr_left: 'ลาออกแล้ว', p_hr_not_in_sso: 'ไม่อยู่ในประกันสังคม',
  p_hr_joined_this_month: 'เริ่มงานเดือนนี้',
  p_hr_left_this_month: 'ลาออกเดือนนี้',
  p_hr_change_salary: 'ปรับเงินเดือน', p_hr_leaving: 'ลาออก',
  p_hr_sso_title: 'ประกันสังคมเดือนนี้',
  p_hr_sso_how: 'ทั้งสองฝ่ายจ่ายฝ่ายละ {{rate}}% ของเงินเดือน เงินเดือนต่ำกว่า {{floor}} คิดที่ {{floor}} และแต่ละฝ่ายสูงสุด {{cap}} เกินจากนั้นเงินสมทบจะไม่เพิ่มขึ้น ส่วนของพนักงานหักจากเงินเดือน นายจ้างจ่ายสมทบเท่ากัน เดือนใดที่มีการจ้างงานแม้เพียงวันเดียวจะนับทั้งเดือน ทั้งเดือนที่เริ่มงานและเดือนที่ลาออก',
  p_hr_check_these: 'ควรตรวจสอบเดือนนี้:',
  p_hr_mover_note: 'ผู้ที่เริ่มงานหรือลาออกระหว่างเดือนคือกรณีที่ตัวเลขอัตโนมัติมักต้องให้คนตรวจ หากจำนวนควรต่างจากนี้ โปรดแจ้ง เราจะตั้งค่าด้วยมือ',
  p_hr_employee_side: 'พนักงาน', p_hr_employer_side: 'นายจ้าง', p_hr_total: 'รวม',
  p_hr_month_total: 'รวม · {{count}} คน', p_hr_manual: 'ตั้งด้วยมือ',
  p_hr_no_sso: 'เดือนนี้ไม่มีพนักงานที่ต้องส่งประกันสังคม',
  p_hr_snapshot: 'เดือนนี้คงที่แล้ว เป็นข้อมูลที่ยื่นไป',
  p_hr_preview: 'เดือนนี้ยังไม่ได้ยื่น คำนวณจากรายชื่อพนักงานปัจจุบันและอาจเปลี่ยนแปลงได้',
  p_hr_year_title: 'ประกันสังคมตลอดปี {{year}}',
  p_hr_month: 'เดือน', p_hr_headcount: 'จำนวนคน', p_hr_wages: 'ค่าจ้างรวม',
  p_hr_filed_snap: 'คงที่', p_hr_preview_short: 'ประมาณการ',
  p_hr_requests: 'คำขอของท่าน',
  p_hr_k_salary_change: 'ปรับเงินเดือน', p_hr_k_employee_add: 'พนักงานใหม่', p_hr_k_employee_leave: 'พนักงานลาออก',
  p_hr_s_pending: 'รอ {{brand}} ดำเนินการ', p_hr_s_applied: 'ดำเนินการแล้ว', p_hr_s_rejected: 'ไม่ได้ดำเนินการ',
  p_hr_request_title: 'ส่งคำขอแก้ไข',
  p_hr_request_note: 'การส่งคำขอยังไม่เปลี่ยนแปลงข้อมูลใด {{brand}} จะตรวจสอบก่อน เพราะประกันสังคมและ ภ.ง.ด.1 จะเปลี่ยนตาม',
  p_hr_kind: 'ประเภทการแก้ไข', p_hr_employee: 'พนักงาน', p_hr_pick_employee: 'เลือก…',
  p_hr_start_date: 'วันเริ่มงาน', p_hr_last_day: 'วันทำงานวันสุดท้าย',
  p_hr_new_salary: 'เงินเดือนใหม่', p_hr_effective_from: 'มีผลตั้งแต่',
  p_hr_note: 'ข้อมูลเพิ่มเติม', p_hr_cancel: 'ยกเลิก', p_hr_send: 'ส่งคำขอ', p_hr_sending: 'กำลังส่ง…',
  p_hr_sso_preview: 'ที่เงินเดือนนี้ ประกันสังคมฝ่ายพนักงาน {{employee}} และนายจ้างเท่ากัน รวมเดือนละ {{total}}',
  p_hr_err: 'ส่งไม่สำเร็จ โปรดลองอีกครั้ง',
  // ── PandaAI Tax (อ่านอย่างเดียว) ──
  p_nav_tax: 'ภาษี',
  p_tax_title: 'การยื่นภาษี',
  p_tax_subtitle: 'ภาษีทุกประเภทที่เรายื่นให้ท่าน: ที่มาของตัวเลข จำนวนเงิน ใครชำระอะไรแล้วบ้าง และเอกสารที่ยื่น',
  p_tax_loading: 'กำลังโหลด…',
  p_tax_pick: 'เลือกประเภทภาษีทางด้านซ้าย',
  p_tax_not_configured: '{{brand}} ไม่ได้ดำเนินการรายการนี้',
  p_tax_nothing_filed: 'ยังไม่มีการยื่น',
  p_tax_half_year: 'ยื่นปีละสองครั้ง',
  p_tax_annual: 'ยื่นปีละครั้ง',
  p_tax_calc: 'วิธีคำนวณ',
  p_tax_no_calc: 'ไม่มีรายละเอียดการคำนวณสำหรับงวดนี้',
  p_tax_amount: 'จำนวนที่ต้องชำระ',
  p_tax_you_paid_slf: 'ท่านชำระให้ {{brand}}',
  p_tax_slf_paid_rd: '{{brand}} นำส่งกรมสรรพากร',
  p_tax_settled: 'ชำระครบแล้ว',
  p_tax_outstanding: 'คงค้าง',
  p_tax_nothing_billed: 'ไม่มีการเรียกเก็บสำหรับงวดนี้',
  p_tax_remitted: 'นำส่งแล้ว',
  p_tax_not_remitted: 'ยังไม่ได้นำส่ง',
  p_tax_filed: 'ยื่นแบบแล้ว',
  p_tax_method: 'วิธีชำระเงิน',
  p_tax_method_na: 'ระบบไม่ได้บันทึกไว้',
  p_tax_docs: 'เอกสารที่ยื่น',
  p_tax_no_docs: 'ยังไม่มีเอกสารสำหรับงวดนี้',
  p_tax_l_out_base: 'ยอดขาย (ไม่รวม VAT)',
  p_tax_l_out_vat: 'ภาษีขาย',
  p_tax_l_in_base: 'ยอดซื้อ (ไม่รวม VAT)',
  p_tax_l_in_vat: 'ภาษีซื้อ',
  p_tax_l_net: 'ภาษีขาย − ภาษีซื้อ',
  p_tax_l_brought: 'เครดิตยกมา',
  p_tax_l_carry: 'เครดิตยกไปงวดหน้า',
  p_tax_l_surcharge: 'เงินเพิ่ม',
  p_tax_l_fines: 'ค่าปรับ',
  p_tax_l_headcount: 'จำนวนพนักงาน',
  p_tax_l_gross_wages: 'ค่าจ้างรวม',
  // ── บริการของ SLF (อ่านอย่างเดียว) ──
  p_menu_services: 'บริการของเรา',
  p_menu_services_hint: 'เราทำอะไร ค่าบริการเท่าไร และขั้นตอนเป็นอย่างไร',
  p_svc_title: 'บริการของ {{brand}}',
  p_svc_subtitle: 'รายการบริการทั้งหมด พร้อมราคาของท่านหากได้ตกลงไว้ กดที่บริการเพื่อดูขั้นตอน',
  p_svc_search: 'ค้นหาบริการ…',
  p_svc_loading: 'กำลังโหลด…',
  p_svc_none: 'ไม่พบบริการที่ตรงกัน',
  p_svc_yours: 'ของท่าน',
  p_svc_your_price: 'ราคาของท่าน',
  p_svc_list_price: 'ราคาตามรายการ',
  p_svc_how: 'ขั้นตอนของบริการนี้:',
  p_svc_steps_loading: 'กำลังโหลดขั้นตอน…',
  p_svc_no_steps: 'ยังไม่ได้บันทึกขั้นตอนของบริการนี้',
  p_svc_step_you: 'ท่าน',
  p_svc_step_slf: '{{brand}}',
  // ── งานที่ SLF ดำเนินการ (อ่านอย่างเดียว) ──
  p_nav_jobs: 'งาน',
  p_jobs_title: 'งานที่ {{brand}} ดำเนินการ',
  p_jobs_subtitle: 'งานที่เรากำลังดำเนินการให้ท่าน และสถานะของแต่ละงาน ดูได้อย่างเดียว',
  p_jobs_loading: 'กำลังโหลด…',
  p_jobs_all: 'ทั้งหมด',
  p_jobs_no: 'เลขที่',
  p_jobs_what: 'รายการ',
  p_jobs_status: 'สถานะ',
  p_jobs_due_date: 'กำหนด / เสร็จ',
  p_jobs_amount: 'จำนวนเงิน',
  p_jobs_paid: 'ชำระแล้ว',
  p_jobs_outstanding: 'คงค้าง',
  p_jobs_date_tbc: 'รอยืนยัน',
  p_jobs_waiting_on: 'รอ',
  p_jobs_opened: 'เปิดงาน',
  p_jobs_updated: 'อัปเดต',
  p_jobs_st_not_started: 'ยังไม่เริ่ม',
  p_jobs_st_in_progress: 'กำลังดำเนินการ',
  p_jobs_st_with_you: 'รอท่านดำเนินการ',
  p_jobs_st_with_authority: 'รอหน่วยงานราชการ',
  p_jobs_st_with_third_party: 'รอบุคคลภายนอก',
  p_jobs_st_done: 'เสร็จสิ้น',
  p_jobs_st_cancelled: 'ยกเลิก',
  // bs — bank statements the client sends in (owner 2026-10-13)
  p_bs_title: 'ส่งรายการเดินบัญชีธนาคารของท่านมาให้เรา',
  p_bs_subtitle: 'รายการเดินบัญชี (statement) ของบัญชีธนาคารของท่านเอง จากธนาคารโดยตรง {{brand}} จะจับคู่แต่ละรายการกับใบแจ้งหนี้และใบเสร็จของท่านด้วยมือ — การอัปโหลดที่นี่ไม่ได้เปลี่ยนแปลงบัญชีหรือแบบแสดงรายการภาษีของท่านโดยตัวมันเอง',
  p_bs_account: 'บัญชีธนาคาร',
  p_bs_account_auto: 'ตรวจหาจากไฟล์อัตโนมัติ',
  p_bs_no_accounts: 'ยังไม่มีบัญชีธนาคารในข้อมูลบริษัทของท่าน — กรุณาเพิ่มบัญชีที่หน้าข้อมูลบริษัทก่อน เพื่อให้ระบบจดจำรายการเดินบัญชีได้',
  p_bs_pw_label: 'รหัสผ่านไฟล์ PDF',
  p_bs_pw_ph: 'กรอกเฉพาะเมื่อธนาคารตั้งรหัสผ่านไฟล์',
  p_bs_pw_hint: 'ใช้เพื่อเปิดไฟล์นี้เท่านั้น ไม่มีการจัดเก็บ',
  p_bs_upload: '＋ อัปโหลดรายการเดินบัญชี',
  p_bs_uploading: 'กำลังอ่านไฟล์…',
  p_bs_file_hint: 'ไฟล์ PDF จากธนาคารหรือไฟล์ CSV ที่ส่งออกมา — ครั้งละไม่เกิน {{max}} ไฟล์ ไฟล์ละไม่เกิน {{mb}} MB รูปถ่ายและ Excel อ่านไม่ได้',
  p_bs_too_many: 'กรุณาส่งครั้งละไม่เกิน {{max}} ไฟล์',
  p_bs_upload_failed: 'อัปโหลดไม่สำเร็จ',
  p_bs_res_saved: '✓ {{filename}} — บัญชี {{account}} · อ่านได้ {{rows}} รายการ ใหม่ {{added}} รายการ มีอยู่แล้ว {{skipped}} รายการ',
  p_bs_res_failed: '✗ {{filename}} — {{error}}',
  p_bs_err_account_not_registered: 'บัญชีในรายการเดินบัญชีนี้ยังไม่ได้ลงทะเบียนในข้อมูลบริษัทของท่าน — กรุณาเพิ่มบัญชีก่อน',
  p_bs_err_statement_account_mismatch: 'รายการเดินบัญชีนี้เป็นของบัญชีอื่น ไม่ใช่บัญชีที่ท่านเลือก',
  p_bs_list_title: 'รายการเดินบัญชีที่ได้รับ',
  p_bs_th_file: 'ไฟล์',
  p_bs_th_account: 'บัญชี',
  p_bs_th_period: 'ช่วงเวลา',
  p_bs_th_rows: 'รายการ',
  p_bs_rows_cell: 'ใหม่ {{added}} จาก {{rows}}',
  p_bs_closing: 'ยอดคงเหลือปลายงวด {{amount}}',
  p_bs_th_uploaded: 'อัปโหลดเมื่อ',
  p_bs_via_portal: 'โดยท่าน',
  p_bs_via_workspace: 'โดย {{brand}}',
  p_bs_act_view: 'ดู',
  p_bs_act_withdraw: 'ถอนออก',
  p_bs_confirm_withdraw: 'ถอน {{filename}} ออกหรือไม่? รายการที่ยังไม่ได้จับคู่จะถูกลบไปด้วย',
  p_bs_withdraw_locked: 'ถอนรายการเดินบัญชีนี้ไม่ได้ — {{brand}} ได้จับคู่บางรายการไปแล้ว',
  p_bs_empty: 'ยังไม่มีรายการเดินบัญชี',
  p_bs_err_open: 'ไม่สามารถเปิดไฟล์ได้',
  p_bs_file_fallback: 'รายการเดินบัญชี',
  // btn
  p_btn_cancel: 'ยกเลิก',
  p_btn_save: 'บันทึก',
  p_btn_save_bilingual: 'บันทึก',
  // cdoc
  p_cdoc_add_line: '+ เพิ่มรายการ',
  p_cdoc_bank_cash: 'ชำระเป็นเงินสด — ไม่มีข้อมูลบัญชีธนาคาร',
  p_cdoc_bank_label: 'บัญชีธนาคารที่แสดงบน {{docKind}} นี้',
  p_cdoc_bank_none: '— ยังไม่มีบัญชี (เพิ่มได้ที่ ข้อมูลบริษัท) —',
  p_cdoc_choose_service: '— เลือกบริการ —',
  p_cdoc_correct_prompt: 'เหตุใดจึงแก้ไข {{docNo}}?\n\nระบบจะบันทึกเหตุผลนี้ไว้กับเอกสารพร้อมกับตัวเลขเดิม',
  p_cdoc_currency: 'สกุลเงิน',
  p_cdoc_remark_ph: 'หมายเหตุที่พิมพ์ลงในเอกสาร — การจัดรูปแบบ การขึ้นบรรทัดใหม่ และรูปภาพที่วางจะถูกเก็บไว้',
  p_cdoc_pay_method: 'เงื่อนไขการชำระเงิน',
  p_cdoc_pay_method_none: '— ไม่ระบุ —',
  p_cdoc_pm_tt: 'T/T — การโอนเงินผ่านธนาคาร',
  p_cdoc_pm_lc: 'L/C — เลตเตอร์ออฟเครดิต',
  p_cdoc_pm_sblc: 'SBLC — สแตนด์บายเลตเตอร์ออฟเครดิต',
  p_cdoc_pm_bg: 'BG — หนังสือค้ำประกันธนาคาร',
  p_cdoc_pay_method_note: 'จะพิมพ์ไว้ในหัวเอกสาร และแสดงชื่อเต็มข้างข้อมูลธนาคาร',
  p_cdoc_pay_method_blank: 'เอกสารภายในประเทศโดยทั่วไปเว้นว่างไว้ เลือกเมื่อชำระผ่านเครื่องมือทางธนาคาร',
  p_cdoc_customer: 'ลูกค้า *',
  p_cdoc_doc_type: 'ประเภทเอกสาร',
  p_cdoc_doc_type_locked: '— กำหนดตายตัวเมื่อออกเอกสารแล้ว',
  p_cdoc_due_date: 'วันครบกำหนดชำระ *',
  p_cdoc_due_hint: '— วันที่ออก + {{days}} วัน',
  p_cdoc_edit_note: 'เลขที่เอกสารยังคงเป็น {{docNo}} หากเอกสารฉบับนี้ส่งให้ลูกค้าไปแล้ว กรุณาส่งฉบับที่แก้ไขแล้วให้ลูกค้าอีกครั้ง',
  p_cdoc_entered_incl: 'ยอดที่กรอก (รวม VAT) {{amount}} — แยกภาษีด้านล่าง',
  p_cdoc_err_due: 'ต้องระบุวันครบกำหนดชำระ — ใบแจ้งหนี้นี้ครบกำหนดชำระเมื่อใด',
  p_cdoc_err_due_before: 'วันครบกำหนดชำระต้องไม่ก่อนวันที่ออกเอกสาร',
  p_cdoc_err_line: 'ทุกรายการต้องระบุบริการ — กรุณาเลือกบริการ หรือลบรายการที่ว่างออก',
  p_cdoc_err_reason: 'ต้องระบุเหตุผลในการแก้ไขเอกสารทางภาษี',
  p_cdoc_fx_date: 'วันที่อัตราแลกเปลี่ยน',
  p_cdoc_fx_note: 'โดยปกติใบกำกับภาษีต้องออกเป็นเงินบาท การใช้สกุลเงินต่างประเทศต้องได้รับอนุมัติจากกรมสรรพากร (มาตรา 86/4) โดยเอกสารจะแสดงจำนวนเงินเทียบเท่าเป็นเงินบาทกำกับไว้',
  p_cdoc_fx_ph: 'เช่น 33.50',
  p_cdoc_fx_rate: 'อัตราแลกเปลี่ยนเป็น$t(cur_word) *',
  p_cdoc_issue_btn: 'ออก{{docKind}}',
  p_cdoc_issue_date: 'วันที่ออกเอกสาร',
  p_cdoc_issuing: 'กำลังออกเอกสาร…',
  p_cdoc_items: 'รายการ',
  p_cdoc_less_wht: 'หักภาษี ณ ที่จ่าย {{amount}}',
  p_cdoc_net: 'ยอดสุทธิที่ได้รับ',
  p_cdoc_qty_title: 'จำนวน',
  p_cdoc_remark: 'หมายเหตุ (ไม่บังคับ)',
  p_cdoc_save_changes: 'บันทึกการแก้ไข',
  p_cdoc_saving: 'กำลังบันทึก…',
  p_cdoc_subtotal: 'รวมเป็นเงิน',
  p_cdoc_title_edit: 'แก้ไข {{docKind}} {{docNo}}',
  p_cdoc_title_new: '{{docKind}}ใหม่',
  p_cdoc_total: 'รวมทั้งสิ้น',
  p_cdoc_type_invoice: 'ใบแจ้งหนี้ — งานบริการ ออกใบกำกับภาษีเมื่อได้รับชำระเงิน',
  p_cdoc_type_proforma: 'ใบแจ้งหนี้ชั่วคราว (Proforma Invoice) — ไม่ใช่ใบกำกับภาษี ยังไม่มีความรับผิดในการเสีย VAT',
  p_cdoc_type_tax_invoice: 'ใบกำกับภาษี/ใบส่งของ — ความรับผิดในการเสียภาษีมูลค่าเพิ่มเกิดขึ้นเมื่อส่งมอบสินค้า',
  p_cdoc_type_tir: 'ใบกำกับภาษี/ใบเสร็จรับเงิน — ขายเงินสด',
  p_cdoc_unitprice_title: 'ราคาต่อหน่วย',
  p_cdoc_valid_until: 'ยืนราคาถึงวันที่',
  p_cdoc_vat: 'VAT',
  p_cdoc_vat_title: 'การคิดภาษีมูลค่าเพิ่ม',
  // crec
  p_crec_already_paid: 'ชำระแล้ว',
  p_crec_attach_hint: 'แนบหนังสือรับรอง 50 ทวิ ของผู้ซื้อได้ภายหลังจากหน้ารายการใบเสร็จรับเงิน',
  p_crec_cash: 'เงินที่ได้รับจริง ($t(cur_word))',
  p_crec_date_ok: 'ใบเสร็จรับเงินจะลงวันที่และออกเลขที่ในเดือน {{yyyymm}}',
  p_crec_date_required: 'จำเป็นต้องระบุ — ให้ดูวันที่จากรายการเดินบัญชีธนาคาร ไม่ใช่วันที่ท่านกรอกข้อมูล',
  p_crec_differs: '· ไม่ตรงกับยอดค้างชำระ {{amount}} — ระบบจะบันทึกเป็นการชำระบางส่วน',
  p_crec_err: 'ไม่สำเร็จ',
  p_crec_expected_wht: 'ภาษีหัก ณ ที่จ่าย (ประมาณ)',
  p_crec_invoice: 'ใบแจ้งหนี้ *',
  p_crec_invoice_total: 'ยอดรวมตามใบแจ้งหนี้',
  p_crec_issue: 'ออกใบเสร็จรับเงิน',
  p_crec_option_outstanding: 'คงค้าง {{amount}} $t(cur_word)',
  p_crec_outstanding: 'ยอดค้างชำระ',
  p_crec_payment_date: 'วันที่รับชำระ (วันที่เงินเข้าบัญชี) *',
  p_crec_saving: 'กำลังบันทึก…',
  p_crec_select_invoice: '— เลือกใบแจ้งหนี้ที่ยังไม่ได้ชำระ —',
  p_crec_settling: 'ตัดชำระ {{total}} = เงินสด {{cash}} + หัก ณ ที่จ่าย {{wht}}',
  p_crec_subtitle: 'เมื่อเลือกใบแจ้งหนี้ ระบบจะเติมยอดคงค้างและภาษีหัก ณ ที่จ่ายที่คาดว่าผู้ซื้อจะหักไว้ให้อัตโนมัติ หากเงินที่ได้รับจริงไม่ตรงกัน ท่านสามารถแก้ไขตัวเลขทั้งสองได้',
  p_crec_title: 'บันทึกรับชำระเงิน',
  p_crec_wht: 'ภาษีหัก ณ ที่จ่ายที่ผู้ซื้อหักไว้',
  // cust
  p_cust_add: '+ เพิ่มลูกค้า',
  p_cust_confirm_remove: 'ลบ {{name}} หรือไม่?',
  p_cust_empty: 'ยังไม่มีลูกค้า — เพิ่มผู้ซื้อรายแรกของท่าน',
  p_cust_f_address: 'ที่อยู่',
  p_cust_f_branch: 'สาขา',
  p_cust_f_email: 'อีเมล',
  p_cust_f_name: 'ชื่อบริษัท / ชื่อบุคคล *',
  p_cust_f_name_th: 'ชื่อภาษาไทย (ไม่บังคับ)',
  p_cust_f_notes: 'หมายเหตุ',
  p_cust_f_notes_hint: '— ใช้ภายในเท่านั้น ไม่แสดงบนเอกสาร',
  p_cust_f_phone: 'โทรศัพท์',
  p_cust_f_taxid: 'เลขประจำตัวผู้เสียภาษี',
  p_cust_modal_edit: 'แก้ไขข้อมูลลูกค้า',
  p_cust_modal_new: 'ลูกค้าใหม่',
  p_cust_search_ph: 'ค้นหาชื่อ รหัส เลขประจำตัวผู้เสียภาษี อีเมล…',
  p_cust_th_branch: 'สาขา',
  p_cust_th_email: 'อีเมล',
  p_cust_th_name: 'ชื่อ',
  p_cust_th_no: 'เลขที่',
  p_cust_th_taxid: 'เลขประจำตัวผู้เสียภาษี',
  p_cust_title: 'ลูกค้าของฉัน',
  // disc
  p_disc_accept: 'ข้าพเจ้าได้อ่านและยอมรับข้อความข้างต้น',
  p_disc_body_bold: 'ท่านเป็นผู้รับผิดชอบแต่เพียงผู้เดียวต่อความถูกต้องและความต่อเนื่องของเลขที่เอกสารของท่าน',
  p_disc_body_p1: 'เอกสารขายของท่านทั้งหมด (ใบเสนอราคา ใบแจ้งหนี้ ใบเสร็จรับเงิน) ต้องออกผ่านระบบนี้ เพื่อให้เลขที่เอกสารเรียงต่อเนื่องกัน เลขที่เอกสารจะไม่ถูกนำกลับมาใช้ซ้ำ และเมื่อยกเลิกเอกสาร เลขที่นั้นจะยังคงอยู่ในลำดับโดยมีสถานะ VOID',
  p_disc_body_p2: ' รวมถึงเอกสารที่ท่านออกนอกระบบนี้ด้วย',
  p_disc_continue: 'ดำเนินการต่อ',
  p_disc_title: 'การออกเลขที่เอกสาร — เป็นความรับผิดชอบของท่าน',
  // doc
  p_doc_act_attach_wht: 'แนบ 50 ทวิ',
  p_doc_act_attach_wht_title: 'แนบหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) จากผู้ซื้อ',
  p_doc_act_delete: 'ลบและคืนเลขที่เอกสาร',
  p_doc_act_edit: 'แก้ไข',
  p_doc_act_pdf: 'PDF',
  p_doc_act_send: 'ส่งไปยัง…',
  p_doc_act_send_title: 'ส่งอีเมลเอกสารนี้ให้ลูกค้า พร้อมแนบไฟล์ PDF',
  p_doc_act_void: 'ยกเลิกเอกสาร',
  p_doc_add_new: '+ สร้าง{{docKind}}ใหม่',
  p_doc_add_note: '+ สร้างใบลดหนี้ / ใบเพิ่มหนี้',
  p_doc_add_receipt: '+ บันทึกรับชำระเงิน',
  p_doc_confirm_handed_over: '{{docNo}} ถูกดาวน์โหลดเป็น PDF ไปแล้ว ผู้ซื้ออาจถือสำเนาฉบับนั้นอยู่\n\nการแก้ไขจะทำให้เอกสารไม่ตรงกับสำเนาที่ผู้ซื้อถืออยู่ หากผู้ซื้อได้รับเอกสารไปแล้ว ควรออกใบลดหนี้/ใบเพิ่มหนี้แทน หรือยกเลิกแล้วออกใหม่\n\nยืนยันที่จะแก้ไขหรือไม่?',
  p_doc_delete_prompt: 'ลบ {{docNo}} และคืนเลขที่เอกสารกลับเข้าระบบหรือไม่?\n\nคืนได้เฉพาะเลขล่าสุดของชุดเอกสารนั้นเท่านั้น เอกสารฉบับถัดไปที่ท่านออกจะใช้เลข {{docNo}} ซ้ำ\n\nเหตุผล? (ระบบจะบันทึกไว้)',
  p_doc_empty: 'ยังไม่มีข้อมูล',
  p_doc_empty_notes: 'ยังไม่มีใบลดหนี้หรือใบเพิ่มหนี้ ออกเอกสารได้จากใบกำกับภาษีที่ต้องแก้ไข',
  p_doc_err_open: 'ไม่สามารถเปิดเอกสารนี้ได้',
  p_doc_freed: '{{docNo}} กลับมาว่างแล้ว — เอกสารฉบับถัดไปจะใช้เลขที่นี้',
  p_doc_head_invoices: 'ใบแจ้งหนี้',
  p_doc_head_notes: 'ใบลดหนี้ / ใบเพิ่มหนี้',
  p_doc_head_quotations: 'ใบเสนอราคา',
  p_doc_head_receipts: 'ใบเสร็จรับเงิน',
  p_doc_kind_invoice: 'ใบแจ้งหนี้',
  p_doc_kind_quotation: 'ใบเสนอราคา',
  p_doc_not_editable: 'เอกสารนี้ไม่สามารถแก้ไขได้',
  p_doc_note_credit: 'ใบลดหนี้',
  p_doc_note_debit: 'ใบเพิ่มหนี้',
  p_doc_reason_required: 'ต้องระบุเหตุผล',
  p_doc_search_ph: 'ค้นหาเลขที่เอกสาร ลูกค้า ที่อยู่…',
  p_doc_send_tooltip: 'ส่งแล้ว {{count}} ครั้ง · ครั้งล่าสุดถึง {{email}}',
  p_doc_status_open: 'เปิดอยู่',
  p_doc_status_paid: 'ชำระแล้ว',
  p_doc_status_partial: 'ชำระบางส่วน',
  p_doc_status_unpaid: 'ยังไม่ชำระ',
  p_doc_status_void: 'VOID',
  p_doc_th_adjusts: 'ปรับปรุงใบกำกับภาษี',
  p_doc_th_customer: 'ลูกค้า',
  p_doc_th_date: 'วันที่',
  p_doc_th_difference: 'ผลต่าง ($t(cur_word))',
  p_doc_th_no: 'เลขที่',
  p_doc_th_due: '\u0e04\u0e23\u0e1a\u0e01\u0e33\u0e2b\u0e19\u0e14',
  p_doc_th_owing: '\u0e04\u0e07\u0e04\u0e49\u0e32\u0e07',
  p_sum_n_inv: '{{count}} \u0e43\u0e1a',
  p_sum_oldest: '\u0e19\u0e32\u0e19\u0e2a\u0e38\u0e14 {{days}} \u0e27\u0e31\u0e19 \u2014 {{docNo}}',
  p_sum_outstanding: '\u0e23\u0e2d\u0e23\u0e31\u0e1a\u0e0a\u0e33\u0e23\u0e30',
  p_sum_overdue: '\u0e40\u0e01\u0e34\u0e19\u0e01\u0e33\u0e2b\u0e19\u0e14',
  p_sum_scope: '\u0e17\u0e38\u0e01\u0e0a\u0e48\u0e27\u0e07\u0e40\u0e27\u0e25\u0e32 \u00b7 \u0e13 \u0e27\u0e31\u0e19\u0e19\u0e35\u0e49',
  p_doc_th_invoice_no: 'ใบแจ้งหนี้',
  p_conv_already: 'ออกใบแจ้งหนี้จากใบเสนอราคานี้แล้ว: {{list}} ออกเพิ่มได้ — ใบเสนอราคาหนึ่งใบออกใบแจ้งหนี้ได้หลายครั้ง — แต่ลูกค้าจะได้รับใบแจ้งหนี้อีกฉบับ',
  p_conv_cancel: 'ยกเลิก',
  p_conv_done: 'ออกใบแจ้งหนี้ {{docNo}} เรียบร้อยแล้ว',
  p_conv_go: 'ออกใบแจ้งหนี้',
  p_conv_loading: 'กำลังตรวจสอบใบเสนอราคา…',
  p_conv_receipts: 'เงินที่รับมาแล้วตามใบเสนอราคานี้ ({{list}}) จะอยู่ที่เดิม ใบแจ้งหนี้ใบใหม่จึงออกเต็มจำนวนและยังไม่ชำระ',
  p_conv_subtitle: 'ใบแจ้งหนี้จะคัดลอกทุกรายการจากใบเสนอราคา ลงวันที่วันนี้ และเริ่มต้นที่สถานะยังไม่ชำระ',
  p_conv_title: 'ออกใบแจ้งหนี้จาก {{docNo}}',
  p_conv_type_invoice: 'ใบแจ้งหนี้ (Invoice)',
  p_conv_type_invoice_hint: 'ใบวางบิล สำหรับบริการ ภาษีมูลค่าเพิ่มถึงกำหนดเมื่อได้รับชำระ (ม.78/1) ใบกำกับภาษีจึงออกพร้อมใบเสร็จรับเงิน',
  p_conv_type_label: 'เอกสารนี้เป็นประเภทใด?',
  p_conv_type_locked: 'เมื่อออกแล้วแก้ไม่ได้ — กำหนดจุดที่ความรับผิดภาษีเกิดขึ้น หากต้องแก้ต้องยกเลิกและออกใหม่',
  p_conv_type_proforma: 'ใบแจ้งหนี้ชั่วคราว (Proforma)',
  p_conv_type_proforma_hint: 'ไม่ใช่เอกสารทางภาษี ไม่ทำให้เกิดจุดที่ความรับผิดภาษี และไม่บันทึกบัญชี',
  p_conv_type_tax_invoice: 'ใบกำกับภาษี (Tax invoice)',
  p_conv_type_tax_invoice_hint: 'สำหรับขายสินค้า ภาษีมูลค่าเพิ่มถึงกำหนดเมื่อส่งมอบสินค้า (ม.78) ภาษีขายจึงบันทึกที่เอกสารฉบับนี้ — ในเดือนนี้',
  p_conv_working: 'กำลังออก…',
  p_doc_act_to_invoice: 'ออกใบแจ้งหนี้',
  p_doc_link_self: 'เอกสารฉบับนี้',
  p_doc_th_property: 'ทรัพย์สิน',
  p_doc_th_receipt_no: 'ใบเสร็จรับเงิน',
  p_doc_th_received: 'รับชำระ ($t(cur_word))',
  p_doc_th_sent: 'ส่งแล้ว',
  p_doc_th_status: 'สถานะ',
  p_doc_th_total: 'รวมทั้งสิ้น',
  p_doc_th_wht: 'WHT',
  p_doc_void_prompt: 'ยกเลิก {{docNo}} หรือไม่?\n\nเลขที่เอกสารจะยังคงอยู่ในลำดับโดยมีสถานะ VOID และจะไม่ถูกนำกลับมาใช้ซ้ำ\n\nเหตุผลในการยกเลิกคืออะไร (ระบบจะบันทึกไว้)',
  p_doc_wht_saved: 'บันทึกหนังสือรับรองการหักภาษี ณ ที่จ่ายแล้ว ✓',
  // empty
  p_empty_no_match: 'ไม่พบข้อมูลที่ตรงกับ “{{query}}”',
  // erec
  p_erec_bank: 'บัญชีธนาคารที่แสดงบนเอกสาร',
  p_erec_bank_none: '— ไม่ระบุ —',
  p_erec_received: 'รับชำระ {{amount}}',
  p_erec_remark: 'หมายเหตุ',
  p_erec_saving: 'กำลังบันทึก…',
  p_erec_title: 'แก้ไขใบเสร็จรับเงิน {{docNo}}',
  p_erec_warning: 'ใบเสร็จรับเงินฉบับนี้เป็นใบกำกับภาษี ชื่อลูกค้า จำนวนเงิน ภาษีมูลค่าเพิ่ม และวันที่ เป็นข้อมูลที่ผู้ซื้อใช้เป็นภาษีซื้อ จึงแก้ไขที่นี่ไม่ได้ — หากต้องการแก้ไขข้อมูลเหล่านี้ ให้ยกเลิกใบเสร็จรับเงินฉบับนี้แล้วบันทึกการรับชำระเงินใหม่',
  p_erec_wht: '· หัก ณ ที่จ่าย {{amount}}',
  // err
  p_err_create_failed: 'สร้างไม่สำเร็จ',
  p_err_save_failed: 'บันทึกไม่สำเร็จ',
  // exp
  p_exp_act_remove: 'ลบ',
  p_exp_act_rescan: 'อ่านตัวเลขใหม่',
  p_exp_act_rescan_title: 'อ่านตัวเลขจากไฟล์สแกนนี้ใหม่อีกครั้ง',
  p_exp_act_view: 'ดูไฟล์แนบ',
  p_exp_confirm_remove: 'ลบ {{filename}} หรือไม่?',
  p_exp_empty: 'ยังไม่มีการอัปโหลดสำหรับเดือน {{month}} ปี {{year}}',
  p_exp_err_open: 'ไม่สามารถเปิดไฟล์ได้',
  p_exp_file_fallback: 'ไฟล์',
  p_exp_file_hint: 'รองรับเฉพาะรูปภาพและไฟล์ PDF — ไม่สามารถอ่านไฟล์ Word และ Excel ได้ กรุณาบันทึกเป็น PDF ก่อน เอกสารที่มีหลายหน้าให้ส่งเป็นไฟล์ PDF เดียว ไม่ใช่แยกเป็นรูปหลายรูป',
  p_exp_filed_warn: 'แบบ ภ.พ.30 ของเดือน {{month}} {{year}} ได้ยื่นไปแล้ว เอกสารที่ท่านส่งเข้ามาสำหรับเดือนนี้จะถูกบันทึกเป็นการยื่นเพิ่มเติม ไม่ได้รวมอยู่ในแบบที่ยื่นไปแล้ว',
  p_exp_filed_warn_bold: 'การยื่นเพิ่มเติม',
  p_exp_filter_all: 'ทุกสถานะ',
  p_exp_filter_approved: 'บันทึกบัญชีแล้ว',
  p_exp_filter_pending: 'รอ {{brand}} ดำเนินการ',
  p_exp_filter_rejected: 'ส่งคืนให้ท่าน',
  p_exp_list_title: 'อัปโหลดแล้ว — {{month}} {{year}}',
  p_exp_month: 'เดือน',
  p_exp_msg_appended: '· {{filename}} — เพิ่มเป็นหน้า {{page}} ของบิลเลขที่ {{docNo}}',
  p_exp_msg_ocr_error: 'มีบางไฟล์ที่ระบบอ่านอัตโนมัติไม่ได้ — {{brand}} จะกรอกตัวเลขให้ด้วยตนเอง',
  p_exp_msg_saved: 'ได้รับเอกสาร {{n}} ฉบับแล้ว อยู่ระหว่างรอ {{brand}} ดำเนินการ',
  p_exp_msg_skipped: 'ข้าม {{n}} รายการเนื่องจากซ้ำ:',
  p_exp_msg_skipped_line: '· {{filename}}{{docNo}} — {{why}}',
  p_exp_no_vat: 'ไม่มี VAT',
  p_exp_ocr_error_row: 'ไม่สามารถอ่านข้อมูลอัตโนมัติได้',
  p_exp_page_chip: 'น.{{n}}',
  p_exp_page_title: 'หน้า {{n}}',
  p_exp_quota_unlimited: 'อัปโหลดแล้ว {{used}} รายการในเดือน{{month}} {{year}} · ไม่จำกัดจำนวน',
  p_exp_quota_full: 'ถึงขีดจำกัด {{cap}} รายการต่อเดือนของเดือน{{month}} {{year}} แล้ว — หากต้องการส่งจำนวนมากกว่านี้ กรุณาอีเมลถึง {{contact}}',
  p_exp_quota_left: 'ใช้ไปแล้ว {{used}} จาก {{cap}} สำหรับเดือน {{month}} {{year}} · เหลือ {{left}}',
  p_exp_reading: 'กำลังอ่านเอกสาร…',
  p_exp_search_ph: 'ค้นหาผู้ขาย เลขที่ใบแจ้งหนี้ ชื่อไฟล์…',
  p_exp_status_approved: 'บันทึกบัญชีแล้ว',
  p_exp_status_pending: 'รอ {{brand}} ตรวจสอบ',
  p_exp_status_rejected: 'ส่งกลับให้ท่าน',
  p_exp_subtitle: 'รูปถ่ายหรือไฟล์ PDF ของบิลค่าใช้จ่ายที่ท่านชำระแล้ว {{brand}} จะตรวจสอบเอกสารแต่ละฉบับกับภาพสแกนก่อนบันทึกเข้าบัญชีของท่าน — การอัปโหลดที่นี่ไม่ได้เปลี่ยนแปลงแบบแสดงรายการภาษีของท่านโดยตัวมันเอง · ส่งใบกำกับภาษีซื้อของท่าน',
  p_exp_th_amount: 'จำนวนเงิน',
  p_exp_th_attachment: 'ไฟล์แนบ',
  p_exp_th_bucket: 'กลุ่มภาษี',
  p_exp_th_date: 'วันที่',
  p_exp_th_docno: 'เลขที่ใบกำกับภาษี',
  p_exp_th_status: 'สถานะ',
  p_exp_th_supplier: 'ผู้ขาย',
  p_exp_th_vat: 'VAT',
  p_exp_title: 'ส่งบิลซื้อ/ใบกำกับภาษีซื้อของท่านมาให้เรา',
  p_exp_upload: '＋ อัปโหลดใบกำกับภาษีซื้อ',
  p_exp_upload_failed: 'อัปโหลดไม่สำเร็จ',
  p_exp_with_vat: 'มี VAT',
  p_exp_year: 'ปี',
  // fil
  p_fil_chip_additional: 'A{{n}}',
  p_fil_chip_attachment: 'เอกสารแนบ',
  p_fil_chip_form: 'แบบยื่น',
  p_fil_chip_receipt: 'ใบเสร็จรับเงิน',
  p_fil_legend: 'เขียว = ยื่นแล้ว มีเอกสารให้ดูด้านล่าง · แบบยื่น = แบบแสดงรายการภาษีที่ยื่นแล้ว · ใบเสร็จรับเงิน = ใบเสร็จรับเงินค่าภาษีจากกรมสรรพากร · ✓ = ยื่นแล้ว แต่ยังไม่ได้อัปโหลดเอกสาร หากไม่พบเอกสารที่ต้องการ กรุณาติดต่อ {{brand}} แล้วเราจะเพิ่มให้',
  p_fil_loading: 'กำลังโหลด…',
  p_fil_subtitle: 'แบบภาษีที่ {{brand}} ยื่นให้ท่าน แยกตามเดือน คลิกที่เดือนเพื่อเปิดแบบที่ยื่นแล้วหรือใบเสร็จรับเงินของกรมสรรพากร',
  p_fil_th_filing: 'แบบแสดงรายการภาษี',
  p_fil_title: 'ภาษีและการยื่นแบบ',
  // filing
  p_filing_doc_gone: 'เอกสารนี้ไม่พร้อมใช้งานแล้ว กรุณาติดต่อ {{brand}}',
  // img
  p_img_err_generic: 'ไม่สามารถอ่านรูปภาพนี้ได้',
  p_img_err_not_image: 'ไฟล์นี้ไม่ใช่รูปภาพ',
  p_img_err_read: 'ไม่สามารถอ่านไฟล์ได้',
  p_img_none: 'ไม่มี',
  p_img_remove: 'ลบออก',
  p_img_replace: 'เปลี่ยนรูป',
  p_img_upload: 'อัปโหลด',
  // imp
  p_imp_already_bold: 'มีเลขที่เอกสารซ้ำอยู่แล้ว {{n}} รายการ',
  p_imp_already_rest: 'และจะทำให้การนำเข้าหยุดลง: {{list}}',
  p_imp_btn: 'นำเข้า',
  p_imp_btn_n: 'นำเข้า {{n}} รายการ',
  p_imp_confirm: 'สร้าง{{docTypeWord}}จำนวน {{n}} ฉบับ โดยใช้เลขที่เอกสารในไฟล์หรือไม่?\n\nระบบจะสร้างข้อมูลลูกค้าใหม่ {{m}} ราย\n\nไม่สามารถย้อนกลับทั้งชุดได้ — ต้องยกเลิกเอกสารทีละฉบับ',
  p_imp_create_as: 'สร้างเป็น',
  p_imp_done: '✓ สร้างใบแจ้งหนี้ {{created}} ฉบับ · ลูกค้าใหม่ {{customers}} ราย',
  p_imp_done_btn: 'เสร็จสิ้น',
  p_imp_err_failed: 'นำเข้าไม่สำเร็จ',
  p_imp_err_read: 'ไม่สามารถอ่านไฟล์นั้นได้',
  p_imp_open: 'นำเข้าจากสเปรดชีต',
  p_imp_map_title: 'จับคู่คอลัมน์ในสเปรดชีตกับฟิลด์ของระบบ',
  p_imp_map_saved: '· ใช้การจับคู่ที่คุณบันทึกไว้สำหรับชีตนี้',
  p_imp_map_edit: 'แก้ไขการจับคู่',
  p_imp_map_hide: 'ซ่อนการจับคู่',
  p_imp_map_th_column: 'คอลัมน์ในสเปรดชีต',
  p_imp_map_th_sample: 'ตัวอย่างข้อมูล',
  p_imp_map_th_field: 'จับคู่เป็น',
  p_imp_map_th_vat: 'การคิด VAT',
  p_imp_map_th_label: 'ชื่อรายการในใบแจ้งหนี้',
  p_imp_map_no_header: 'คอลัมน์ที่ {{n}} (ไม่มีหัวตาราง)',
  p_imp_map_required: 'ยังไม่ได้จับคู่คอลัมน์ใดกับ "{{field}}" ซึ่งทุกเอกสารต้องมี',
  p_imp_map_need_amount: 'ต้องจับคู่อย่างน้อยหนึ่งคอลัมน์ที่เป็นจำนวนเงิน เพราะเป็นรายการในใบแจ้งหนี้',
  p_imp_field_ignore: 'ไม่นำเข้า',
  p_imp_field_customer: 'ชื่อลูกค้า',
  p_imp_field_docNo: 'เลขที่ใบแจ้งหนี้',
  p_imp_field_issueDate: 'วันที่ออก',
  p_imp_field_dueDate: 'วันครบกำหนด',
  p_imp_field_address: 'ที่อยู่บนเอกสาร',
  p_imp_field_description: 'รายละเอียด',
  p_imp_field_remark: 'หมายเหตุ',
  p_imp_field_fee: 'จำนวนเงิน (เป็นหนึ่งรายการ)',
  p_imp_field_total: 'ยอดรวมในไฟล์ (ใช้ตรวจทานเท่านั้น)',
  p_imp_fee_q: 'ค่าธรรมเนียมแต่ละคอลัมน์เสียภาษีอย่างไร?',
  p_imp_fee_warn: '— หากกรอกผิด จะทำให้ยอดที่ผู้ซื้อทุกรายต้องชำระคลาดเคลื่อน 7%',
  p_imp_file_label: 'ไฟล์สเปรดชีต (.xlsx)',
  p_imp_header_incl: '· หัวคอลัมน์ระบุว่า “รวม VAT”',
  p_imp_header_unknown: '· หัวคอลัมน์ไม่ได้ระบุ — โปรดยืนยัน',
  p_imp_mismatch_body: 'ใบแจ้งหนี้จะออกตามคอลัมน์ค่าบริการด้านบน ส่วนยอดรวมในไฟล์อาจเป็นคนละตัวเลข กรุณาตรวจสอบบางรายการก่อนนำเข้า:',
  p_imp_mismatch_bold: '{{n}} แถวไม่ตรงกับคอลัมน์ยอดรวมในไฟล์',
  p_imp_mismatch_line: 'แถว {{n}} · {{docNo}} · ใบแจ้งหนี้ {{a}} เทียบกับไฟล์ {{b}}',
  p_imp_more: '… และอีก {{n}} รายการ',
  p_imp_prev_th_customer: 'ลูกค้า',
  p_imp_prev_th_date: 'วันที่',
  p_imp_prev_th_lines: 'รายการ',
  p_imp_prev_th_total: 'รวม',
  p_imp_sheet: 'ชีต',
  p_imp_skipped_head: 'ข้าม — รายการเหล่านี้จะไม่ถูกนำเข้า',
  p_imp_skipped_row: 'แถว {{n}}',
  p_imp_skipped_title: 'แถวที่ข้าม — ไม่ได้สร้างเอกสารสำหรับรายการเหล่านี้:',
  p_imp_subtitle: 'หนึ่งแถวต่อหนึ่งใบแจ้งหนี้ เลขที่เอกสาร วันที่ และจำนวนเงิน จะถูกนำมาจากไฟล์ของท่านตามเดิมทุกประการ ไม่มีการสร้างขึ้นใหม่หรือปรับแก้',
  p_imp_summary: 'จะสร้างใบแจ้งหนี้ {{rows}} ใบ · ลูกค้าใหม่ {{customers}} ราย · รวม {{amount}} $t(cur_word)',
  p_imp_summary_skipped: 'ข้าม {{skipped}} แถว',
  p_imp_th_docno: 'เลขที่ใบแจ้งหนี้',
  p_imp_th_owner: 'เจ้าของ',
  p_imp_th_property: 'ทรัพย์สิน',
  p_imp_th_row: 'แถว',
  p_imp_th_why: 'เหตุผล',
  p_imp_title: 'นำเข้าใบแจ้งหนี้จากไฟล์สเปรดชีต',
  p_imp_type_inv: 'ใบแจ้งหนี้ — วางบิลเท่านั้น ใบกำกับภาษีจะออกเมื่อได้รับชำระเงิน',
  p_imp_type_ti: 'ใบกำกับภาษี / ใบส่งของ — ความรับผิดในการเสียภาษีมูลค่าเพิ่มเกิดขึ้นเมื่อส่งมอบสินค้า',
  p_imp_type_tir: 'ใบกำกับภาษี/ใบเสร็จรับเงิน — ขายเงินสด ชำระเงินแล้ว',
  p_imp_word_invoices: 'ใบแจ้งหนี้',
  p_imp_word_tax_invoices: 'ใบกำกับภาษี',
  p_imp_working: 'กำลังดำเนินการ…',
  // lang
  p_lang_en: 'English',
  p_lang_en_short: 'EN',
  p_lang_th_short: 'ไทย',
  p_lang_zh_short: '中',
  // loading
  p_loading: 'กำลังโหลด…',
  // login
  p_login_email: 'อีเมล',
  p_login_err_failed: 'เข้าสู่ระบบไม่สำเร็จ',
  p_login_err_staff: 'นี่คือพอร์ทัลสำหรับลูกค้า — พนักงานกรุณาใช้ระบบภายใน',
  // Cross-installation sign-in (owner 2026-10-15)
  p_login_err_blank: 'กรุณากรอกอีเมลและรหัสผ่าน',
  p_login_hop_text: 'บัญชีนี้อยู่กับสำนักงานอีกแห่งของบริษัท ({{host}}) ต้องการเข้าสู่ระบบที่นั่นหรือไม่?',
  p_login_hop_go: 'ใช่ เข้าสู่ระบบที่นั่น',
  p_login_hop_cancel: 'ยกเลิก',
  p_login_password: 'รหัสผ่าน',
  p_login_signin: 'เข้าสู่ระบบ',
  p_login_signing_in: 'กำลังเข้าสู่ระบบ…',
  p_login_subtitle: 'ระบบออกเอกสารสำหรับธุรกิจของท่าน',
  p_login_title: 'พอร์ทัลลูกค้า {{brand}}',
  // logo
  p_logo_alt: '{{brand}}',
  // menu
  p_menu_actions_tooltip: 'การดำเนินการ',
  p_menu_bills: 'ใบวางบิลจาก {{brand}}',
  p_menu_bills_hint: 'ค่าบริการที่ {{brand}} เรียกเก็บจากท่าน',
  p_menu_customers: 'ลูกค้า',
  p_menu_customers_hint: 'ผู้ซื้อที่ท่านออกใบแจ้งหนี้ให้',
  p_menu_filings: 'ภาษีและการยื่นแบบ',
  p_menu_filings_hint: 'แบบแสดงรายการภาษีที่ {{brand}} ยื่นให้ท่าน',
  p_menu_group_fromslf: 'จาก {{brand}}',
  p_menu_group_mydata: 'ข้อมูลของฉัน',
  p_menu_group_company: 'บริษัท',
  p_menu_products: 'สินค้าและบริการ',
  p_menu_products_hint: 'สิ่งที่ท่านขาย',
  p_menu_profile: 'ข้อมูลบริษัท',
  p_menu_profile_hint: 'ที่อยู่ โลโก้ การออกเลขที่เอกสาร',
  p_menu_tooltip: 'เมนู',
  p_menu_working: 'กำลังดำเนินการ…',
  // month
  p_month_apr: 'เม.ย.',
  p_month_aug: 'ส.ค.',
  p_month_dec: 'ธ.ค.',
  p_month_feb: 'ก.พ.',
  p_month_jan: 'ม.ค.',
  p_month_jul: 'ก.ค.',
  p_month_jun: 'มิ.ย.',
  p_month_mar: 'มี.ค.',
  p_month_may: 'พ.ค.',
  p_month_nov: 'พ.ย.',
  p_month_oct: 'ต.ค.',
  p_month_sep: 'ก.ย.',
  // nav
  p_nav_bankstmt: 'รายการเดินบัญชี',
  p_nav_expenses: 'ใบกำกับภาษีซื้อ',
  p_office_label: 'สำนักงาน {{brand}} ของท่าน',
  p_office_hint: 'สำนักงาน {{brand}} ที่ดูแลบัญชีและออกเอกสารให้ท่าน',
  p_nav_owner: 'แดชบอร์ดผู้บริหาร',
  p_own_title: 'แดชบอร์ดผู้บริหาร',
  p_own_lede: 'ภาพรวมธุรกิจของคุณ — ใครค้างชำระคุณ คุณค้างเท่าไร และอะไรถึงกำหนดถัดไป',
  p_own_empty: 'ยังไม่มีข้อมูลแสดงที่นี่ หน้านี้จะเริ่มมีข้อมูลเมื่อคุณออกใบแจ้งหนี้ หรือเมื่อเราเริ่มยื่นแบบให้คุณ',
  p_own_owed_you: 'ลูกค้าค้างชำระคุณ',
  p_own_overdue_suffix: 'เกินกำหนด',
  p_own_chase: 'ค้างนานที่สุด',
  p_own_days_late: 'เกินกำหนด {{days}} วัน',
  p_own_owed_slf: 'คุณค้างชำระ {{brand}}',
  p_own_next_due: 'ครบกำหนดถัดไป {{date}}',
  p_own_waiting: 'รอคุณดำเนินการ',
  p_own_and_more: 'และอีก {{n}} รายการ',
  p_own_task_upload: 'ส่งเอกสารประจำเดือนให้เรา',
  p_own_task_tax_pay: 'ชำระภาษี',
  p_own_task_svc_pay: 'ชำระค่าบริการของเรา',
  p_own_task_info: 'ส่งข้อมูลที่เราขอ',
  p_own_upcoming: 'ถึงกำหนดถัดไป',
  p_own_today: 'วันนี้',
  p_own_in_days: 'อีก {{days}} วัน',
  p_own_amount_tbc: 'รอสรุปยอด',
  p_own_amount_credit: 'เครดิตยกไป {{amount}}',
  p_own_efiling_note: 'วันที่แสดงคือกำหนดยื่นแบบออนไลน์ของกรมสรรพากร ซึ่งช้ากว่ากำหนดยื่นที่สำนักงาน',
  p_own_booked: 'บันทึกบัญชีปี {{year}}',
  p_own_revenue: 'ยอดขาย',
  p_own_purchases: 'ยอดซื้อ',
  p_own_booked_note: 'จาก {{months}} เดือนที่บันทึกแล้ว ไม่ใช่ตัวเลขกำไร เงินเดือนจะรวมเมื่อปิดเดือนแล้วเท่านั้น',
  p_own_filed: 'ยื่นแบบให้คุณแล้วในปี {{year}}',
  p_own_filed_note: 'แบบที่เรายื่นแล้ว',
  p_nav_books: 'สมุดบัญชีของคุณ',
  p_menu_company_find: 'ค้นหาบริษัท…',
  p_menu_books_locked: 'จะแสดงเมื่อเปิดบัญชีของคุณแล้ว',
  p_books_period: 'งวดบัญชี',
  p_menu_company_none: 'ไม่พบบริษัทที่ตรงกัน',
  p_menu_company_n_of: '{{n}} จาก {{total}}',
  p_menu_policy: 'เราบันทึกบัญชีของคุณอย่างไร',
  p_menu_policy_hint: 'แนวปฏิบัติที่เราใช้กับเอกสารของคุณจริง',
  p_menu_chart: 'ผังบัญชี',
  p_menu_chart_hint: 'บัญชีที่ใช้บันทึกรายการของคุณ',
  p_menu_ledgers: 'บัญชีแยกประเภทและงบทดลอง',
  p_menu_ledgers_hint: 'สมุดรายวันทั้งห้าเล่ม บัญชีแยกประเภท และงบทดลอง',
  p_books_no_entries: 'ไม่มีรายการในงวดนี้',
  p_menu_statements: 'งบการเงิน',
  p_menu_statements_hint: 'งบแสดงฐานะการเงิน งบกำไรขาดทุน งบแสดงการเปลี่ยนแปลงส่วนของเจ้าของ',
  p_pol_title: 'เราบันทึกบัญชีของคุณอย่างไร',
  p_pol_lede: 'หน้านี้อธิบายสิ่งที่ระบบของเราทำกับเอกสารของคุณจริง เป็นการอธิบายการทำงานของระบบ ไม่ใช่การอ้างอิงตัวบทกฎหมาย ในส่วนที่เกี่ยวข้องกับกฎหมาย ให้ถือความเห็นเป็นลายลักษณ์อักษรจากผู้ทำบัญชีของคุณเป็นสำคัญ',
  p_pol_readonly: 'หน้านี้เป็นแบบอ่านอย่างเดียว',
  p_pol_s1_t: 'รายได้ของคุณเข้าบัญชีเมื่อใด',
  p_pol_s1_b: 'ใบกำกับภาษี (รวมใบกำกับภาษี/ใบเสร็จรับเงิน) บันทึกเป็นภาษีขายในเดือนที่ออกเอกสาร ส่วนใบแจ้งหนี้ทั่วไปจะบันทึกต่อเมื่อได้รับชำระเงินและบันทึกใบเสร็จรับเงินแล้วเท่านั้น ดังนั้นใบแจ้งหนี้ที่ยังไม่ได้รับชำระในเดือนนี้จะไม่ปรากฏในฐานภาษีขายของเดือนนี้ และหากไม่ได้รับชำระเลยก็จะไม่ปรากฏเลย',
  p_pol_s2_t: 'ภาษีหัก ณ ที่จ่าย บันทึกเมื่อใด',
  p_pol_s2_b: 'ภาษีที่ลูกค้าหักไว้ เราบันทึกเมื่อได้รับเงิน ไม่ใช่ในเดือนที่ออกใบแจ้งหนี้ ยอดลูกหนี้ของคุณจึงแสดงจำนวนเต็ม ไม่ใช่จำนวนที่จะเข้าบัญชีธนาคารจริง',
  p_pol_s3_t: 'ใบลดหนี้และใบเพิ่มหนี้',
  p_pol_s3_b: 'การแก้ไขมูลค่าของใบกำกับภาษีที่ออกไปแล้ว เราจะออกใบลดหนี้หรือใบเพิ่มหนี้แยกต่างหาก และบันทึกในเดือนของเอกสารนั้นเอง บันทึกของใบกำกับภาษีฉบับเดิมจะไม่ถูกแก้ไข ซึ่งเป็นร่องรอยที่ผู้สอบบัญชีต้องการเห็น',
  p_pol_s4_t: 'เงินตราต่างประเทศ',
  p_pol_s4_b: 'ระบบของเราไม่ได้เก็บอัตราแลกเปลี่ยนไว้เอง อัตราแลกเปลี่ยนบนเอกสารสกุลต่างประเทศกรอกโดยผู้ออกเอกสารเป็นรายฉบับ และอัตราที่ต้องใช้ ณ สิ้นปีต้องมีผู้ให้ข้อมูลแก่เรา หากไม่มีอัตรา เราจะไม่แปลงค่าและจะไม่ประมาณตัวเลขขึ้นมาเอง ยอดลูกหนี้สกุลต่างประเทศจึงแสดงแยกตามสกุลเงิน และไม่รวมเป็นยอดเงิน$t(cur_word)ยอดเดียว',
  p_pol_s5_t: 'ภาษีซื้อที่ขอคืนไม่ได้',
  p_pol_s5_b: 'ภาษีซื้อที่ขอคืนไม่ได้ จะบันทึกเป็นค่าใช้จ่ายในบัญชี 5710 แทนที่จะคงไว้ปะปนกับภาษีซื้อที่ขอคืนได้',
  p_pol_s6_t: 'การตีราคาสินค้าคงเหลือ',
  p_pol_s6_b: 'รองรับวิธีคิดต้นทุนสองวิธี คือ เข้าก่อนออกก่อน (FIFO) และถัวเฉลี่ยถ่วงน้ำหนัก เมื่อตีราคางวดใดด้วยวิธีหนึ่งแล้ว เราไม่สามารถเปลี่ยนให้คุณเป็นอีกวิธีได้ และเมื่อบันทึกสินค้าคงเหลือไม่สอดคล้องกัน เช่น มีการจ่ายออกโดยไม่มีการรับเข้า หรือจ่ายออกมากกว่าที่รับเข้า เราจะไม่ออกผลการตีราคาให้ แทนที่จะให้ตัวเลขที่ไม่มีที่มา',
  p_pol_s7_t: 'ค่าเสื่อมราคา',
  p_pol_s7_b: 'ค่าเสื่อมราคาคำนวณด้วยวิธีเส้นตรง และเฉลี่ยตามจำนวนวันที่คุณถือครองทรัพย์สินจริงภายในงวด โดยคิดปีละ 365 วัน จะไม่ตัดค่าเสื่อมจนต่ำกว่ามูลค่าคงเหลือ และจะไม่ผ่านรายการเอง ทุกรายการจัดทำเป็นฉบับร่างและมีเจ้าหน้าที่ตรวจสอบก่อนเข้าสู่บัญชีของคุณ อัตราขั้นสูงที่เราใช้แสดงด้านล่าง',
  p_pol_s7_note: 'ตารางนี้เป็นอัตราขั้นสูง หากทรัพย์สินใดไม่ได้กำหนดอัตราเฉพาะ จะใช้อัตราขั้นสูงของประเภทนั้น',
  p_pol_s8_t: 'ผลขาดทุนยกไป',
  p_pol_s8_b: 'เราบันทึกผลขาดทุนยกไปของคุณเป็นรายปี ช่องปีที่สิ้นสุดสิทธิแสดงว่า "ยังไม่ได้กำหนด" เนื่องจากเรายังไม่มีแหล่งอ้างอิงที่ยืนยันได้ การบันทึกระยะเวลาดังกล่าวต้องบันทึกฐานทางกฎหมายของมันไปพร้อมกัน',
  p_pol_s9_t: 'เอกสารที่คุณส่งมาจะถูกดำเนินการอย่างไร',
  p_pol_s9_b: 'เอกสารทุกฉบับจะถูกจัดเป็นหนึ่งในสามประเภท จากนั้นจะกลายเป็นรายการฉบับร่างซึ่งมีเจ้าหน้าที่ตรวจสอบก่อนผ่านรายการ รายการที่ผ่านแล้วจะไม่ถูกลบ หากมีข้อผิดพลาดจะแก้ไขด้วยรายการกลับรายการที่มองเห็นได้ ทุกรายการจะลงในสมุดรายวันหนึ่งในห้าเล่ม และรายการธนาคารจะระบุบัญชีที่เกี่ยวข้อง',
  p_pol_th_class: 'ประเภททรัพย์สิน',
  p_pol_th_rate: 'อัตราขั้นสูง',
  p_pol_not_dep: 'ไม่คิดค่าเสื่อมราคา',
  p_books_none_t: 'ยังไม่ได้จัดทำสมุดบัญชีของคุณ',
  p_books_none_b: 'เมื่อเราเปิดงวดบัญชีให้คุณและผ่านรายการชุดแรกแล้ว ผังบัญชี สมุดบัญชี และงบการเงินของคุณจะปรากฏที่นี่',
  p_nav_invoices: 'ใบแจ้งหนี้',
  p_nav_notes: 'ใบลดหนี้ / ใบเพิ่มหนี้',
  p_nav_quotations: 'ใบเสนอราคา',
  p_nav_receipts: 'ใบเสร็จรับเงิน',
  p_nav_sign_out: 'ออกจากระบบ',
  // note
  p_note_amount_ph: 'จำนวนเงิน',
  p_note_difference_label: 'ผลต่างของมูลค่า',
  p_note_err: 'ออกใบลดหนี้/ใบเพิ่มหนี้ไม่สำเร็จ',
  p_note_info: 'กรอกเฉพาะส่วนต่างที่เอกสารนี้ปรับปรุง ไม่ใช่ยอดเต็มของเอกสารต้นฉบับ ภาษีมูลค่าเพิ่มของส่วนต่างนั้นจะไปปรับภาษีขายในเดือนที่ออกเอกสารนี้',
  p_note_issue_btn: 'ออกเอกสาร',
  p_note_issue_date: 'วันที่ออกเอกสาร',
  p_note_issuing: 'กำลังออกเอกสาร…',
  p_note_item_ph: 'รายการที่ต้องการปรับปรุง',
  p_note_kind_credit: 'ใบลดหนี้',
  p_note_kind_debit: 'ใบเพิ่มหนี้',
  p_note_no_sources: 'ยังไม่มีใบกำกับภาษีในระบบ — ใบลดหนี้/ใบเพิ่มหนี้ใช้ปรับปรุงได้เฉพาะเอกสารที่มีภาษีมูลค่าเพิ่มเท่านั้น',
  p_note_opt_credit: 'ใบลดหนี้ — ลดมูลค่าตามเอกสารเดิม',
  p_note_opt_debit: 'ใบเพิ่มหนี้ — เพิ่มมูลค่า',
  p_note_reason_discount: 'ลดราคาสินค้าหรือบริการหลังออกใบกำกับภาษี',
  p_note_reason_extra_goods: 'ส่งมอบสินค้าหรือให้บริการเพิ่มเติม',
  p_note_reason_goods_defective: 'สินค้าชำรุดบกพร่องหรือไม่ตรงตามที่ตกลง',
  p_note_reason_goods_returned: 'สินค้าถูกส่งคืน',
  p_note_reason_label: 'เหตุผล * (จะพิมพ์บนใบลดหนี้/ใบเพิ่มหนี้)',
  p_note_reason_other: 'อื่น ๆ',
  p_note_reason_other_ph: 'ระบุเหตุผล',
  p_note_reason_overcharge: 'คำนวณราคาสูงกว่าที่เป็นจริง',
  p_note_reason_service_not_provided: 'บอกเลิกการให้บริการบางส่วนหรือทั้งหมด',
  p_note_reason_undercharge: 'คำนวณราคาต่ำกว่าที่เป็นจริง',
  p_note_ref: 'ใบกำกับภาษีที่ต้องการปรับปรุง *',
  p_note_title_new: '{{noteKind}}ใหม่',
  p_note_total_difference: 'ส่วนต่าง',
  p_note_type: 'ประเภท *',
  // pager
  p_pager_first: 'หน้าแรก',
  p_pager_last: 'หน้าสุดท้าย',
  p_pager_next: 'ถัดไป',
  p_pager_no_records: 'ไม่มีข้อมูล',
  p_pager_page_of: 'หน้า {{page}} จาก {{pages}}',
  p_pager_per_page: 'ต่อหน้า',
  p_pager_prev: 'ก่อนหน้า',
  p_pager_showing: 'แสดง {{from}}–{{to}} จาก {{total}}',
  // pdf
  p_pdf_failed: 'สร้าง PDF ไม่สำเร็จ',
  // prod
  p_prod_add: '+ เพิ่มรายการ',
  p_prod_confirm_remove: 'ลบ {{name}} หรือไม่?',
  p_prod_empty: 'ยังไม่มีรายการ — เพิ่มสินค้าหรือบริการที่ท่านขาย',
  p_prod_f_name: 'ชื่อ *',
  p_prod_f_price: 'ราคาต่อหน่วย ($t(cur_word))',
  p_prod_f_unit: 'หน่วย (เช่น ชิ้น, ชั่วโมง)',
  p_prod_f_vat_checkbox: 'มี VAT 7%',
  p_prod_f_wht: 'ภาษีหัก ณ ที่จ่ายเริ่มต้น (WHT)',
  p_prod_f_wht_hint: 'สินค้าโดยทั่วไป 0% บริการ 3% ค่าเช่า 5% ผู้ซื้อจะหักไว้และออกหนังสือรับรองการหักภาษี ณ ที่จ่าย (50 ทวิ) ให้ท่าน',
  p_prod_modal_edit: 'แก้ไขรายการ',
  p_prod_modal_new: 'รายการใหม่',
  p_prod_photo_hint: 'แสดงในรายการสินค้าของท่าน',
  p_prod_photo_label: 'รูปภาพ (ไม่บังคับ)',
  p_prod_search_ph: 'ค้นหาชื่อหรือหน่วย…',
  p_prod_th_name: 'ชื่อ',
  p_prod_th_price: 'ราคา ($t(cur_word))',
  p_prod_th_unit: 'หน่วย',
  p_prod_th_vat: 'VAT',
  p_prod_title: 'สินค้า/บริการของฉัน',
  p_prod_vat_yes: '{{rate}}%',
  // prof
  p_prof_address: 'ที่อยู่ที่แสดงบนเอกสาร',
  p_prof_biz_goods: 'สินค้า — ความรับผิดในการเสียภาษีมูลค่าเพิ่มเกิดขึ้นเมื่อส่งมอบสินค้า จึงต้องออกใบกำกับภาษีพร้อมกับการส่งมอบ (มาตรา 78)',
  p_prof_biz_hint: 'ใช้กำหนดประเภทเอกสารเริ่มต้นตอนสร้างเอกสารเท่านั้น — ท่านเปลี่ยนได้ในแต่ละฉบับ',
  p_prof_biz_mixed: 'ทั้งสินค้าและบริการ',
  p_prof_biz_service: 'บริการ — ความรับผิดในการเสียภาษีมูลค่าเพิ่มเกิดขึ้นเมื่อได้รับชำระเงิน (มาตรา 78/1)',
  p_prof_biz_type: 'ประเภทธุรกิจ',
  p_prof_company_name: 'ชื่อบริษัท (แก้ไขไม่ได้)',
  p_prof_default_wht: 'ภาษีหัก ณ ที่จ่ายเริ่มต้นของรายการใหม่',
  p_prof_default_wht_hint: 'ใช้เมื่อรายการนั้นไม่ได้อ้างอิงสินค้า/บริการในระบบ',
  p_prof_doc_cn: 'ใบลดหนี้',
  p_prof_doc_dn: 'ใบเพิ่มหนี้',
  p_prof_doc_inv: 'ใบแจ้งหนี้',
  p_prof_doc_pro: 'ใบแจ้งหนี้ชั่วคราว (Proforma)',
  p_prof_doc_quo: 'ใบเสนอราคา',
  p_prof_doc_rec: 'ใบเสร็จรับเงิน',
  p_prof_doc_tax: 'ใบกำกับภาษี',
  p_prof_email: 'อีเมล',
  p_prof_failed: 'ไม่สำเร็จ',
  p_prof_identity_note: 'ข้อมูลแสดงตัวตนได้รับการตรวจสอบจากเอกสารบริษัทของท่าน และแก้ไขได้โดยทีมงานของเราเท่านั้น',
  p_prof_logo_hint: 'พิมพ์ที่มุมบนซ้ายของใบแจ้งหนี้ ใบเสนอราคา และใบเสร็จรับเงิน',
  p_prof_logo_label: 'โลโก้บริษัท',
  p_prof_month_seg: 'ใส่ปี-เดือนในเลขที่เอกสาร',
  p_prof_month_seg_hint: '— เปิด: INV-{{yyyymm}}-001 · ปิด: INV-001 ปิดตัวเลือกนี้หากเลขที่เอกสารของท่านมีเดือน/ปีอยู่แล้ว',
  p_prof_numbering: 'การออกเลขที่เอกสาร',
  p_prof_numbering_note: '— ความต่อเนื่องของเลขที่เอกสารเป็นความรับผิดชอบของท่าน (ยอมรับเมื่อ {{date}})',
  p_prof_phone: 'โทรศัพท์',
  p_prof_prefix_next: '{{docType}} — คำนำหน้า / เลขที่ถัดไป',
  p_prof_pw_changed: 'เปลี่ยนรหัสผ่านเรียบร้อย ✓',
  p_prof_saved: 'บันทึกแล้ว ✓',
  p_prof_branch: 'สถานประกอบการ (แก้ไขไม่ได้)',
  p_prof_branch_head: 'สำนักงานใหญ่',
  p_prof_branch_hint: 'พิมพ์ถัดจากเลขประจำตัวผู้เสียภาษีบนใบกำกับภาษี ใบลดหนี้ และใบเพิ่มหนี้',
  p_prof_branch_n: 'สาขา',
  p_prof_tax_id: 'เลขประจำตัวผู้เสียภาษี (แก้ไขไม่ได้)',
  p_prof_title: 'ข้อมูลบริษัท',
  p_prof_vat_hint: 'จดทะเบียน ⇒ ใบแจ้งหนี้ของท่านจะคิดภาษีมูลค่าเพิ่ม 7% และใบเสร็จรับเงินของท่านจะเป็นใบกำกับภาษี',
  p_prof_vat_not_registered: 'ไม่ได้จดทะเบียน',
  p_prof_vat_reg: 'การจดทะเบียนภาษีมูลค่าเพิ่ม (VAT)',
  p_prof_vat_registered: 'จดทะเบียนภาษีมูลค่าเพิ่ม (VAT 7%)',
  p_prof_website: 'เว็บไซต์',
  // pw
  p_pw_confirm: 'ยืนยันรหัสผ่านใหม่',
  p_pw_current: 'รหัสผ่านปัจจุบัน',
  p_pw_err_current_required: 'กรุณากรอกรหัสผ่านปัจจุบัน',
  p_pw_err_failed: 'ไม่สำเร็จ',
  p_pw_err_min_length: 'อย่างน้อย 8 ตัวอักษร',
  p_pw_err_mismatch: 'รหัสผ่านไม่ตรงกัน',
  p_pw_hint_current: 'ต้องกรอกรหัสผ่านปัจจุบัน',
  p_pw_hint_first: 'เพื่อความปลอดภัย กรุณาตั้งรหัสผ่านใหม่แทนรหัสชั่วคราว',
  p_pw_new: 'รหัสผ่านใหม่',
  p_pw_title_change: 'เปลี่ยนรหัสผ่าน',
  p_pw_title_first: 'ตั้งรหัสผ่านของท่านเอง',
  // row
  p_row_delete: 'ลบ',
  p_row_edit: 'แก้ไข',
  // search
  p_search_clear: 'ล้าง',
  // select
  p_select_placeholder: '— เลือก —',
  p_pick_search: 'พิมพ์เพื่อค้นหา…',
  p_pick_none: 'ไม่พบข้อมูล — ลองใช้คำน้อยลง',
  p_pick_more: 'แสดง {{shown}} จาก {{total}} — พิมพ์เพื่อค้นหาที่เหลือ',
  p_cust_add_title: 'เพิ่มลูกค้า',
  p_cust_add_save: 'เพิ่มและเลือก',
  p_cust_name_req: 'ต้องระบุชื่อลูกค้า',
  p_cust_add_failed: 'เพิ่มลูกค้าไม่สำเร็จ',
  p_prof_f_prefix: 'คำนำหน้า',
  p_prof_f_next: 'เลขถัดไป',
  p_prof_f_date: 'วันที่ในเลขที่เอกสาร',
  p_prof_f_reset: 'เริ่มนับใหม่',
  p_prof_f_pad: 'จำนวนหลัก',
  p_prof_date_none: 'ไม่มีวันที่',
  p_prof_reset_none: 'ไม่เริ่มใหม่',
  p_prof_reset_year: 'ทุกปี',
  p_prof_reset_month: 'ทุกเดือน',
  p_prof_reset_day: 'ทุกวัน',
  p_prof_scheme_bad: 'ต้องมี {{need}} ในเลขที่เอกสาร มิฉะนั้นเลขเดิมจะซ้ำในรอบถัดไป',
  p_prof_numbering_hint: 'เลขที่เอกสาร = คำนำหน้า · วันที่ · เลขลำดับ การเริ่มนับใหม่ต้องมีวันที่ที่แยกรอบได้: รายปีต้องมี YYYY รายเดือน YYYYMM รายวัน YYYYMMDD การแก้ไขมีผลกับเอกสารที่ออกใหม่เท่านั้น เอกสารที่ออกไปแล้วคงเลขเดิม',
  p_flt_all: 'ทั้งหมด',
  p_flt_custom: 'กำหนดเอง',
  p_flt_clear: 'ล้างตัวกรองวันที่',
  p_flt_period: 'ช่วงเวลา',
  p_flt_show_voided: 'แสดงที่ยกเลิกแล้ว',
  p_imp_open_short: 'นำเข้า',
  p_menu_notes_hint: 'แก้ไขเอกสารที่ออกไปแล้ว',
  p_flt_this_month: 'เดือนนี้',
  p_flt_last_month: 'เดือนที่แล้ว',
  p_flt_from: 'ตั้งแต่วันที่',
  p_flt_to: 'ถึงวันที่',
  // send
  p_send_attachment: 'ไฟล์แนบ',
  p_send_bcc: 'สำเนาถึงท่าน',
  p_send_btn: 'ส่งอีเมล',
  p_send_done: 'เสร็จสิ้น',
  p_send_err_prepare: 'ไม่สามารถเตรียมอีเมลได้',
  p_send_err_send: 'ไม่สามารถส่งอีเมลได้',
  p_send_from: 'จาก',
  p_send_history: 'ประวัติการส่ง',
  p_send_message: 'ข้อความ',
  p_send_no_replyto: '— กรุณาเพิ่มอีเมลบริษัทของท่านในหน้าข้อมูลบริษัทก่อน —',
  p_send_preparing: 'กำลังเตรียม…',
  p_send_preview: 'ดูตัวอย่าง',
  p_send_preview_hint: 'เปิดไฟล์ PDF ฉบับเดียวกับที่ลูกค้าจะได้รับ — ครบทั้งสองฉบับ ทั้งต้นฉบับและสำเนา',
  p_send_replyto: 'ตอบกลับที่',
  p_send_save_addr: 'บันทึกอีเมลนี้ไว้กับ {{customerName}} เพื่อให้กรอกอัตโนมัติในครั้งถัดไป',
  p_send_sending: 'กำลังส่ง…',
  p_send_sent_ok: '✓ ส่งไปยัง {{email}} แล้ว และมีสำเนาอยู่ในกล่องจดหมายของท่าน',
  p_send_subject: 'เรื่อง',
  p_send_subtitle: 'ระบบจะแนบไฟล์ PDF ให้อัตโนมัติ และเมื่อลูกค้าตอบกลับ อีเมลจะกลับมาถึงท่านโดยตรง',
  p_send_title: 'ส่ง {{docNo}}',
  p_send_to_label: 'อีเมลลูกค้า *',
  p_send_to_ph: 'name@example.com',
  // sort
  p_sort_th_tooltip: 'เรียงตาม {{label}}',
  // vat
  p_vat_hint_exempt: 'รายการที่ได้รับยกเว้นภาษีมูลค่าเพิ่ม (มาตรา 81)',
  p_vat_hint_none: 'ไม่อยู่ในบังคับภาษีมูลค่าเพิ่ม / ไม่ได้จดทะเบียนภาษีมูลค่าเพิ่ม',
  p_vat_hint_standard: 'ภาษีมูลค่าเพิ่มจะคิดเพิ่มจากราคาที่ท่านกรอก',
  p_vat_hint_standard_incl: 'ราคาที่ท่านกรอกรวมภาษีมูลค่าเพิ่มแล้ว',
  p_vat_hint_zero: 'อัตราภาษีร้อยละ 0 (มาตรา 80/1) — เสียภาษีในอัตรา 0% ไม่ใช่การยกเว้นภาษี',
  p_vat_opt_exempt: 'ยกเว้นภาษีมูลค่าเพิ่ม',
  p_vat_opt_none: 'ไม่มี VAT',
  p_vat_opt_standard: 'VAT {{rate}}% — แยกนอก',
  p_vat_opt_standard_incl: 'VAT {{rate}}% — รวมใน',
  p_vat_opt_zero: 'VAT 0%',
  // wht
  p_wht_none: 'ไม่หักภาษี ณ ที่จ่าย',
  p_wht_rate: 'หัก ณ ที่จ่าย {{rate}}%',
  p_wht_select_title: 'ภาษีหัก ณ ที่จ่ายที่ผู้ซื้อหักไว้',
};
